// AFTab.js — Счета и фонды (Оригинальный дизайн + Модальные окна и кликабельные транзакции)
import React, { useState, useMemo, useRef } from "react";
import { CreditCard, FolderOpen, Archive, Plus, Minus, Check, X, History, Wallet, Activity, TrendingUp, TrendingDown, AlertCircle, Clock, Users, Tags, Edit3, EyeOff, User, ArrowRight, Sliders, ChevronUp, ChevronDown, Trash2 } from "lucide-react";
import { useTheme, uid, fmt, fmtDate, COLORS, Card, StatCard, Btn, PageHeader, TabSwitch, Confirm, NumInput, addAuditLog, createAuditLogEntry, computeFundWriteoff, AnimNum, TengeIcon, HelpTooltip, SearchableSelect } from "./shared";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import { HistoryModal } from "./HistoryModal";
import { TxEditModal } from "./DataTab"; // Модалка редактирования транзакций
// НОВОЕ: Подключаем тексты подсказок
import { HELP_TEXTS } from "./HelpText";
import ArchiveTab from "./ArchiveTab";
import { S } from './strings';
import { QuickContractorModal } from './OpsTab';

export default function AFTab({ data, uOrg, user, perms, aAccs, aFunds, setTab, onNavigate, onNavigateDebt, isCompact, apiSync, onFundsChanged }) {
  const t = useTheme();
  
  const arAccs = (data.accounts || []).filter(a => a.archived);
  const arFunds = (data.funds || []).filter(f => f.archived);
  const totalArchived = arAccs.length + arFunds.length 
    + (data.contractors||[]).filter(c=>c.archived).length 
    + (data.incomeCategories||[]).filter(c=>c.archived).length 
    + (data.expenseCategories||[]).filter(c=>c.archived).length 
    + (data.transactions||[]).filter(t=>t.archived).length;
    
  const [vw, setVw] = useState('all');
  const [hist, setHist] = useState(null);
  const [confirm, setConfirm] = useState(null);
  
  const [qIncAcc, setQIncAcc] = useState(null);
  const [qExpFund, setQExpFund] = useState(null);
  // НОВОЕ: Стейт для корректировки скрытого счета (заначки)
  const [qHiddenAcc, setQHiddenAcc] = useState(null);
  const [showFundAdjust, setShowFundAdjust] = useState(false);
  
  const [addAcc, setAddAcc] = useState(false);
  const [nAccN, setNAccN] = useState('');
  const [nIban, setNIban] = useState('');
  const [nAccPrimary, setNAccPrimary] = useState(false);
  const [nAccHidden, setNAccHidden] = useState(false);
  // Чанк 2C.2 follow-up (2026-05-13): стартовый баланс при создании счёта.
  const [nAccBal, setNAccBal] = useState('');
  
  const [showFundManager, setShowFundManager] = useState(null); // null | 'manage' | 'add' | {delete: fundId}

  const [activeAcc, setActiveAcc] = useState(null);
  const [activeFund, setActiveFund] = useState(null);
  
  const [editingTx, setEditingTx] = useState(null);
  // Модалка «Будущее пополнение фондов» (расчёт распределения дебиторки)
  const [showDebtFundProj, setShowDebtFundProj] = useState(false);

  // НОВОЕ: Исключаем скрытые счета (isHidden) из общего баланса (tAcc)
  const tAcc = aAccs.filter(a => !a.isHidden).reduce((s, a) => s + (data.accountBalances[a.id] || 0), 0);
  const tFund = aFunds.reduce((s, f) => s + (data.fundBalances[f.id] || 0), 0);
  const tPct = aFunds.reduce((s, f) => s + f.percent, 0);
  const canE = perms?.all || perms?.manageAccounts || perms?.manageFunds;
  // Сортировка: основной счёт всегда первый, скрытые — в конце
  const sorted = [...aAccs].sort((a, b) => {
    if (a.isPrimary && !b.isPrimary) return -1;
    if (!a.isPrimary && b.isPrimary) return 1;
    if (a.isHidden && !b.isHidden) return 1;
    if (!a.isHidden && b.isHidden) return -1;
    return 0;
  });

  const pie = aFunds.map(f => ({ name: f.name, value: Math.max(0, data.fundBalances[f.id] || 0), color: f.color })).filter(d => d.value > 0);
  // НОВОЕ: Исключаем скрытые счета из круговой диаграммы
  const pieAccs = aAccs.filter(a => !a.isHidden).map(a => ({ name: a.name, value: Math.max(0, data.accountBalances[a.id] || 0), color: a.color })).filter(d => d.value > 0);

  // === НОВОЕ: Жестко отсекаем скрытые счета из общей статистики Главной страницы ===
  const hiddenAccountIds = (data.accounts || []).filter(a => a.isHidden).map(a => a.id);
  // Чанк H.1 (2026-05-19): fund-to-hidden переводы ВКЛЮЧАЮТСЯ в "Последние операции"
  // на главной — юзер сознательно перевёл деньги в заначку, он должен видеть movement.
  // Раньше фильтр исключал tx с isHidden=true (frontend ставит этот flag для fund-to-hidden)
  // и tx с toAccountId=hidden. Это скрывало legitimate user-initiated transfer в заначку.
  // Operations ВНУТРИ hidden счёта (например прямой expense из заначки) продолжают
  // скрываться — это другой кейс, обычно для bookkeeper'а а не юзера.
  const visibleTxs = (data.transactions || []).filter(tx => {
    if (tx.archived) return false;
    // Чанк H.2 (2026-05-19): distinguishing fund-to-hidden по полям БД,
    // не по tx.transferScope (frontend-only, теряется после polling refresh).
    // fund-to-hidden = transfer + fundId (фонд-источник) + toAccountId (hidden приёмник).
    // Distinguishing аналогичен backend operations.js Чанк H — consistent across stack.
    const isFundToHidden = tx.type === 'transfer' && tx.fundId && tx.toAccountId
      && hiddenAccountIds.includes(tx.toAccountId);
    if (isFundToHidden) return true;
    // Остальные правила: скрываем если sample/internal hidden
    if (tx.isHidden) return false;
    if (hiddenAccountIds.includes(tx.accountId)) return false;
    if (hiddenAccountIds.includes(tx.toAccountId)) return false;
    return true;
  });

  const dNow = new Date();
  const currentMonthTx = visibleTxs.filter(x => {
    const d = new Date(x.date);
    return d.getFullYear() === dNow.getFullYear() && d.getMonth() === dNow.getMonth();
  });
  const mIncome = currentMonthTx.filter(x => x.type === 'income').reduce((s, x) => s + x.amount, 0);
  const mExpense = currentMonthTx.filter(x => x.type === 'expense').reduce((s, x) => s + x.amount, 0);

  const recent10All = visibleTxs.slice(0, 10);
  const recent10Inc = visibleTxs.filter(x => x.type === 'income').slice(0, 10);
  const recent10Exp = visibleTxs.filter(x => x.type === 'expense').slice(0, 10);

  const accountsById = useMemo(() => {
    const map = {};
    (data.accounts || []).forEach(a => { map[a.id] = a; });
    return map;
  }, [data.accounts]);

  const getTypeLabel = tx => tx.type === 'income' ? S().txTypes.income : tx.type === 'expense' ? S().txTypes.expense : tx.transferScope === 'fund' ? S().af.transferFundShort : S().txTypes.transfer;
  const getTypeColor = tx => tx.type === 'income' ? t.accentGreen : tx.transferScope === 'fund' ? t.accentPurple : tx.type === 'transfer' ? t.accentCyan : t.accentRed;
  // Чанк A.6 (2026-05-15): inline fundName fallback. Backend syncDataMappers возвращает
  // tx.fundName='' (как и categoryName). normalizeOrgData в shared.js делает lookup,
  // но funds=[] при свежей загрузке (Stage 20a Phase 2). Здесь fallback на data.funds
  // который view-layer overlay в App.js mergeт с apiFunds (authoritative из БД).
  const fundNameOf = id => (data.funds || []).find(f => f && f.id === id)?.name || '—';
  // Чанк C (2026-05-15): добавлен показ категории в renderTxList ("Последние операции"
  // на главной). До этого формат был "account · fund" — категория не отображалась,
  // что создавало inconsistency с другими страницами (Операции/Транзакции/TxEditModal).
  const getRouteLabel = tx => {
    if (tx.type === 'expense') {
      const catName = tx.expenseCategoryName || '';
      return `${accountsById[tx.accountId]?.name||'—'} · ${tx.fundName||fundNameOf(tx.fundId)}${catName ? ` · ${catName}` : ''}`;
    }
    if (tx.type === 'income') {
      const catName = tx.incomeCategoryName || '';
      return `${accountsById[tx.accountId]?.name||'—'}${catName ? ` · ${catName}` : ''}`;
    }
    if (tx.transferScope === 'fund') {
      return `${tx.fundName||fundNameOf(tx.fundId)} → ${tx.toFundName||fundNameOf(tx.toFundId)}`;
    }
    return `${accountsById[tx.accountId]?.name||'—'}${tx.type==='transfer'?` → ${accountsById[tx.toAccountId]?.name||'—'}`:''}`;
  };

  // Рендер кликабельных списков транзакций на главной
  const renderTxList = (txList, emptyMsg) => {
    if(txList.length === 0) return <div style={{color:t.textMuted,textAlign:'center',padding:30}}>{emptyMsg}</div>;
    return txList.map(tx => {
      const tc = getTypeColor(tx);
      const label = getTypeLabel(tx);
      const place = getRouteLabel(tx);
      return (
        <div key={tx.id} className="anim-tx" onClick={() => setEditingTx(tx)} style={{padding:'10px 8px',borderBottom:`1px solid ${t.border}`,borderRadius:8,cursor:'pointer',transition:'background .2s'}} onMouseEnter={e=>e.currentTarget.style.background=t.bgInput} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:8,flexWrap:'wrap'}}>
            <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
              <span style={{fontSize:11,padding:'2px 8px',borderRadius:10,fontWeight:700,background:`${tc}14`,color:tc}}>{label}</span>
              <span style={{fontSize:13,color:t.textSec}}>{place}</span>
            </div>
            <span className="mono" style={{fontSize:14,fontWeight:700,color:tc}}>{tx.type === 'income' ? '+' : tx.type === 'transfer' ? '↔' : '−'}{fmt(tx.amount)}</span>
          </div>
        </div>
      );
    });
  };

  // O6 + фича (2026-06-10): sequential создание счёта + авто-распределение стартового баланса
  // по фондам (доход с breakdown). Если распределяем — счёт создаём с balance 0, баланс «приходит»
  // доходом (иначе двойной счёт). Скрытый счёт и счёт без фондов не распределяются.
  const doAddAcc = async () => {
    if(!nAccN.trim()) return alert(S().af.enterAccountName);
    if(!nIban.trim()) return alert(S().af.enterIban);
    const activeAccs = data.accounts.filter(a => !a.archived);
    if (activeAccs.some(a => a.name.trim().toLowerCase() === nAccN.trim().toLowerCase())) return alert(S().af.dupAccName || 'Счёт с таким названием уже существует');
    if (activeAccs.some(a => a.iban.trim().toLowerCase() === nIban.trim().toLowerCase())) return alert(S().af.dupAccIban || 'Счёт с таким IBAN уже существует');
    if (!apiSync || !apiSync.syncCreateAccount) { alert((S().common && S().common.unknownError) || 'Нет связи с сервером'); return; }
    const id = 'acc_' + uid();
    const startBal = parseFloat(nAccBal) || 0;
    const activeFunds = (data.funds || []).filter(f => !f.archived);
    const willDistribute = !nAccHidden && startBal > 0 && activeFunds.length > 0;
    let result = null;
    try {
      result = await apiSync.syncCreateAccount({
        id, name: nAccN.trim(), type: 'cash',
        balance: willDistribute ? 0 : startBal,
        iban: nIban.trim(),
        color: COLORS[(data.accounts || []).length % COLORS.length],
        isHidden: !!nAccHidden, isPrimary: !!nAccPrimary,
      });
    } catch (e) { result = null; }
    if (!result) return;
    if (willDistribute) {
      const bd = [];
      activeFunds.forEach((f, idx) => {
        let pt = Math.round(startBal * f.percent) / 100;
        if (idx === activeFunds.length - 1) {
          const allocated = bd.reduce((s, b) => s + b.amount, 0);
          pt = Math.round((startBal - allocated) * 100) / 100;
        }
        bd.push({ fundId: f.id, fundName: f.name, amount: pt });
      });
      try {
        await apiSync.syncCreateOperation({
          id: `op_${uid()}`, type: 'income', accountId: id, amount: startBal,
          comment: `Пополнение при создании счёта «${nAccN.trim()}» с балансом ${fmt(startBal)}`,
          breakdown: bd, incomeCategoryId: null,
          userId: user?.id, userName: user?.name, date: new Date().toISOString(),
        });
      } catch (e) {}
    }
    addAuditLog(uOrg, user, { type: 'acc_create', desc: `Создан новый счет: ${nAccN.trim()}`, after: { name: nAccN.trim(), iban: nIban.trim(), isPrimary: nAccPrimary, isHidden: nAccHidden, balance: startBal } });
    setNAccN(''); setNIban(''); setNAccPrimary(false); setNAccHidden(false); setNAccBal(''); setAddAcc(false);
    try { window.dispatchEvent(new CustomEvent('inmonex-reload-org')); } catch (e) {}
  };
  // doAddF удалён — добавление фондов через FundManagerModal

  const archAcc = (id) => { 
    const a = data.accounts.find(x => x.id === id);
    if (!a) return;
    const accBalance = parseFloat(data.accountBalances[id] || a.balance || 0) || 0;
    // Чанк J (2026-05-19): если balance != 0 — нужен transfer на target счёт через atomic
    // backend endpoint. Иначе balance "потеряется" в archive (orphan, drift в reconciliation).
    // Старый syncUpdateAccount(isArchived=true) без transfer оставляется как fallback для
    // счетов с balance=0 (нечего переносить).
    if (accBalance !== 0) {
      doArchiveAccountWithTransfer(id, a, accBalance);
    } else {
      doArchiveAccountSimple(id, a);
    }
  };

  // Чанк J: простой archive без transfer (balance=0)
  const doArchiveAccountSimple = (id, a) => {
    uOrg(p => ({...p, accounts: p.accounts.map(x => x.id === id ? { ...x, archived: true } : x)}));
    if (apiSync && apiSync.syncUpdateAccount) {
      apiSync.syncUpdateAccount(id, { isArchived: true });
    }
    addAuditLog(uOrg, user, { type: 'acc_archive', desc: `Счет отправлен в архив: ${a?.name}`, before: { name: a?.name, archived: false }, after: { archived: true } });
    setConfirm(null); setActiveAcc(null);
  };

  // B+C (2026-06-10): удаление счёта «вместе с деньгами» — расход на сумму баланса,
  // распределённый по фондам (computeFundWriteoff), → счёт 0 + фонды −X атомарно на backend,
  // затем архив нулевого счёта. Reconciliation остаётся 0.
  const doDeleteAccountWithFundWriteoff = async (id, accName, accBalance) => {
    if (!apiSync || !apiSync.syncCreateOperation) { window.alert('apiSync недоступен. Обновите страницу.'); return; }
    const X = Math.round((accBalance || 0) * 100) / 100;
    if (X <= 0) { doArchiveAccountSimple(id, data.accounts.find(a => a.id === id)); return; }
    // Балансы фондов хранятся в data.fundBalances (карта id->сумма), не в f.balance — подставляем.
    const fb = data.fundBalances || {};
    const activeFunds = (data.funds || []).filter(f => !f.archived).map(f => ({
      id: f.id, name: f.name, percent: f.percent,
      balance: Math.round(((fb[f.id] != null ? fb[f.id] : (f.balance || 0))) * 100) / 100,
    }));
    if (activeFunds.length === 0) { window.alert('Нет активных фондов для списания.'); return; }
    const { breakdown, remaining } = computeFundWriteoff(X, activeFunds);
    if (Math.abs(remaining) > 0.01) { window.alert('В фондах недостаточно средств для списания этой суммы. Операция отменена.'); return; }
    const expRes = await apiSync.syncCreateOperation({
      id: `op_${uid()}`, type: 'expense', accountId: id, amount: X,
      comment: `Списание при удалении счёта «${accName}»`,
      breakdown, expenseCategoryId: null,
      userId: user?.id, userName: user?.name, date: new Date().toISOString(),
    });
    if (!expRes) return;
    if (apiSync.syncUpdateAccount) { try { await apiSync.syncUpdateAccount(id, { isArchived: true }); } catch (e) {} }
    addAuditLog(uOrg, user, { type: 'acc_archive', desc: `Счёт «${accName}» удалён со списанием ${fmt(X)} ₸ с фондов`, after: { archived: true, writeoff: X } });
    setActiveAcc(null);
    try { window.dispatchEvent(new CustomEvent('inmonex-reload-org')); } catch (e) {}
  };

  // Чанк J: archive с переносом balance через atomic backend endpoint.
  // Показывает Confirm с <select> выбора target счёта. State target хранится в самом
  // confirm объекте чтобы избежать closure issues (action вызывается с current target).
  const doArchiveAccountWithTransfer = (id, a, accBalance) => {
    const candidates = (data.accounts || []).filter(x =>
      !x.archived && !x.isHidden && x.id !== id
    );
    if (candidates.length === 0) {
      window.alert(`Нельзя архивировать счёт "${a.name}" — нет активных счетов для переноса ${fmt(accBalance)} ₸. Сначала создайте другой счёт.`);
      return;
    }
    setConfirm({
      msg: `Архивировать счёт "${a.name}" (${fmt(accBalance)} ₸)?`,
      target: candidates[0].id,  // default первый
      candidates,
      isArchiveTransfer: true,
      accId: id,
      accName: a.name,
      accBalance,
      action: (selectedTarget) => {
        const targetAcc = candidates.find(c => c.id === selectedTarget);
        if (!targetAcc) {
          window.alert('Целевой счёт не найден.');
          return;
        }
        // Чанк J review BLOCKER 2: await-based вместо optimistic.
        // Сначала ждём backend success — если 400 (валидация) или 500 — local
        // state НЕ меняется, alert через handleSyncError. После success — uOrg
        // применяет state. Это медленнее (~200ms), но safer для финансовой
        // операции — нет ложного "успеха" в UI с реальным fail в backend.
        if (!apiSync || !apiSync.syncArchiveAccountWithTransfer) {
          window.alert('apiSync недоступен. Обновите страницу.');
          return;
        }
        apiSync.syncArchiveAccountWithTransfer(id, { balanceTarget: selectedTarget })
          .then((result) => {
            // Backend success — applying local state
            if (!result) return;  // handleSyncError проглотил, alert уже показан
            uOrg(p => ({
              ...p,
              accounts: p.accounts.map(x => x.id === id ? { ...x, archived: true } : x),
              accountBalances: {
                ...p.accountBalances,
                [id]: 0,
                [selectedTarget]: (parseFloat(p.accountBalances[selectedTarget]) || 0) + accBalance,
              },
            }));
            addAuditLog(uOrg, user, {
              type: 'acc_archive',
              desc: `Счёт "${a.name}" архивирован с переносом ${fmt(accBalance)} ₸ → "${targetAcc.name}"`,
              before: { name: a.name, balance: accBalance },
              after: { archived: true, balanceTarget: targetAcc.name },
            });
            setConfirm(null); setActiveAcc(null);
          });
      },
    });
  };
  const archFund = (id) => {
    const f = data.funds.find(x => x.id === id);
    // Stage 20d.1 (2026-05-27): propsFunds из data.funds (merged через apiFunds overlay)
    // вместо p.funds (raw state, может быть stale 30 сек до polling refresh).
    const propsFunds = data.funds || [];
    uOrg(p => ({...p, funds: propsFunds.map(f => f.id === id ? { ...f, archived: true } : f)}));
    // Чанк 20a.4c.1: параллельно пишем в Fund таблицу (БД).
    if (apiSync && apiSync.syncArchiveFund) apiSync.syncArchiveFund(id);
    addAuditLog(uOrg, user, { type: 'fund_archive', desc: `Фонд отправлен в архив: ${f?.name}`, before: { name: f?.name, archived: false }, after: { archived: true } });
    setConfirm(null); setActiveFund(null); 
  };

  return (
    <div>
      {confirm && <Confirm
        msg={confirm.msg}
        extra={
          confirm.isArchiveTransfer ? (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 10 }}>
                Куда перенести {fmt(confirm.accBalance)} ₸?
              </div>
              <select
                value={confirm.target}
                onChange={e => setConfirm(p => ({ ...p, target: e.target.value }))}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgInput, color: t.text, fontSize: 14 }}
              >
                {confirm.candidates.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({fmt(parseFloat(data.accountBalances[c.id] || c.balance || 0))} ₸)
                  </option>
                ))}
              </select>
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${t.border}` }}>
                <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 8 }}>или удалить деньги — списать с фондов (по %, остаток с крупнейшего):</div>
                <button
                  onClick={() => { const cf = confirm; setConfirm(null); doDeleteAccountWithFundWriteoff(cf.accId, cf.accName, cf.accBalance); }}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, background: `${t.accentRed}15`, color: t.accentRed, fontWeight: 700, border: `1px solid ${t.accentRed}40`, cursor: 'pointer' }}
                >
                  Удалить вместе с деньгами
                </button>
              </div>
            </div>
          ) : confirm.extra ? (
            <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 20, lineHeight: 1.5 }}>{confirm.extra}</div>
          ) : null
        }
        onYes={() => {
          if (confirm.isArchiveTransfer) {
            confirm.action(confirm.target);
          } else {
            confirm.action();
            setConfirm(null);
          }
        }}
        onNo={() => setConfirm(null)}
      />}
      {hist && <HistoryModal data={data} targetId={hist.id} targetName={hist.name} onClose={() => setHist(null)} onNavigate={onNavigate} />}
      
      {qIncAcc && <QuickIncomeModal data={data} uOrg={uOrg} user={user} accId={qIncAcc} onClose={() => setQIncAcc(null)} apiSync={apiSync} />}
      {qExpFund && <QuickExpenseModal data={data} uOrg={uOrg} user={user} aAccs={aAccs} fundId={qExpFund} onClose={() => setQExpFund(null)} apiSync={apiSync} />}
      
      {qHiddenAcc && <QuickHiddenAdjustModal data={data} uOrg={uOrg} user={user} apiSync={apiSync} accId={qHiddenAcc.id} type={qHiddenAcc.type} onClose={() => setQHiddenAcc(null)} />}
      {showFundManager && <FundManagerModal data={data} uOrg={uOrg} user={user} aAccs={aAccs} aFunds={aFunds} initialMode={showFundManager} apiSync={apiSync} onFundsChanged={onFundsChanged} onClose={() => setShowFundManager(null)} />}
      {showDebtFundProj && <DebtFundProjectionModal data={data} aFunds={aFunds} t={t} onClose={() => setShowDebtFundProj(false)} />}
      {/* РЕДАКТОР ТРАНЗАКЦИЙ НА ГЛАВНОЙ */}
      {editingTx && <TxEditModal tx={editingTx} data={data} uOrg={uOrg} onClose={() => setEditingTx(null)} t={t} apiSync={apiSync} />}

      {/* МОДАЛКА СЧЕТА */}
      {activeAcc && (
        <AccountModal
          acc={activeAcc} data={data} uOrg={uOrg} t={t} apiSync={apiSync} onSaved={(u) => setActiveAcc(u)}
          onClose={() => setActiveAcc(null)}
          onArchive={() => archAcc(activeAcc.id)}
          onShowHistory={() => { setHist({id: activeAcc.id, name: activeAcc.name}); setActiveAcc(null); }}
        />
      )}

      {/* МОДАЛКА ФОНДА */}
      {activeFund && (
        <FundModal 
          fund={activeFund} data={data} uOrg={uOrg} t={t} tPct={tPct}
          apiSync={apiSync}
          onClose={() => setActiveFund(null)} 
          onArchive={() => { setActiveFund(null); setShowFundManager({ delete: activeFund.id }); }}
          onShowHistory={() => { setHist({id: activeFund.id, name: activeFund.name}); setActiveFund(null); }}
        />
      )}

      <PageHeader
        title={data.name || S().af.defaultCompanyName}
        right={
          <TabSwitch tabs={[
            {id: 'all', label: S().af.tabSummary, icon: Activity},
            {id: 'accounts', label: S().af.tabAccounts, icon: CreditCard},
            {id: 'funds', label: S().af.tabFunds, icon: FolderOpen},
            {id: 'archive', label: S().af.tabArchive(totalArchived), icon: Archive}
          ]} active={vw} onChange={setVw}/>
        }
      />

      {vw === 'all' && (
        <div style={{display:'grid', gridTemplateColumns: isCompact ? '1fr 1fr' : 'repeat(4,1fr)', gap: isCompact ? 8 : 16, marginBottom:24}}>
          <StatCard icon={Wallet} label={S().af.onAccounts} value={<AnimNum value={tAcc}/>} color={t.accent} compact={isCompact}/>
          <StatCard icon={FolderOpen} label={S().af.inFunds} value={<AnimNum value={tFund}/>} color={t.accentPurple} compact={isCompact}/>
          <StatCard icon={TrendingUp} label={S().af.incomeMonth} value={<AnimNum value={mIncome}/>} color={t.accentGreen} compact={isCompact}/>
          <StatCard icon={TrendingDown} label={S().af.expenseMonth} value={<AnimNum value={mExpense}/>} color={t.accentRed} compact={isCompact}/>
        </div>
      )}

      {vw === 'all' && (
        <div style={{display:'flex', flexDirection:'column', gap:18}}>
          {tFund - tAcc > 0.01 && (
            <div style={{padding:16, background:`${t.accentRed}15`, border:`1px solid ${t.accentRed}40`, borderRadius:12, display:'flex', gap:12, alignItems:'center', color:t.accentRed}}>
              <AlertCircle size={28} style={{flexShrink:0}}/>
              <div>
                <div style={{fontWeight:800, fontSize:15}}>{S().af.cashGapTitle}</div>
                <div style={{fontSize:13, marginTop:4, color:t.text}}>{S().af.cashGapDesc(fmt(tFund), fmt(tAcc))}</div>
              </div>
            </div>
          )}

          <div style={{display:'grid', gridTemplateColumns: isCompact ? '1fr' : '1fr 1fr', gap:18}}>
            
            {/* СПИСОК СЧЕТОВ (КЛИКАБЕЛЬНЫЕ СТРОКИ) */}
            <Card>
              <div style={{fontSize: isCompact ? 14 : 16, fontWeight:700, marginBottom:16, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8}}>
                <div style={{display:'flex', alignItems:'center', gap:8}}><CreditCard size={isCompact ? 16 : 18} color={t.accent}/> {S().af.accountsState}</div>
                <div style={{display:'flex', alignItems:'center', gap:10}}>
                  <div className="mono" style={{fontSize: isCompact ? 13 : 15, color:t.accent, fontWeight:800}}><AnimNum value={tAcc}/></div>
                  {perms.manageAccounts && <Btn onClick={() => setAddAcc(!addAcc)} style={{padding:'4px 8px'}}><Plus size={14}/></Btn>}
                </div>
              </div>

              {addAcc && (
                <div style={{display:'flex', flexDirection:'column', gap:10, marginBottom:16, padding:14, background:t.bgInput, borderRadius:12, border:`1px dashed ${t.border}`}}>
                  <div style={{display:'flex', gap:8}}>
                    <input placeholder={S().af.accountNamePlaceholder} value={nAccN} onChange={e=>setNAccN(e.target.value)} style={{flex:1, minWidth:0}}/>
                    <div style={{display:'flex', gap:4}}>
                      <Btn variant="success" onClick={doAddAcc} style={{padding:'6px 10px'}}><Check size={16}/></Btn>
                      <button onClick={()=>setAddAcc(false)} style={{background:'#fee2e2',border:'2px solid #b91c1c',borderRadius:10,padding:5,color:'#b91c1c',cursor:'pointer'}}><X size={16}/></button>
                    </div>
                  </div>
                  <div style={{display:'flex', gap:8, alignItems:'center'}}>
                    <input placeholder={S().af.ibanPlaceholder} value={nIban} onChange={e=>setNIban(e.target.value)} onKeyDown={e=>e.key==='Enter'&&doAddAcc()} style={{flex:1, minWidth:0, fontSize:13}}/>
                    <HelpTooltip text={S().af.ibanTooltip} size={16} />
                  </div>
                  <div style={{display:'flex', gap:8, alignItems:'center'}}>
                    <input type="number" placeholder="Стартовый баланс ₸" value={nAccBal} onChange={e=>setNAccBal(e.target.value)} onKeyDown={e=>e.key==='Enter'&&doAddAcc()} style={{flex:1, minWidth:0, fontSize:13}}/>
                  </div>
                  <div style={{display:'flex', gap:16, flexWrap:'wrap'}}>
                    <label style={{display:'flex', alignItems:'center', gap:6, cursor:'pointer'}}>
                      <input type="checkbox" checked={nAccPrimary} onChange={e=>setNAccPrimary(e.target.checked)} style={{width:15, height:15}}/>
                      <span style={{fontSize:13, fontWeight:600, color:t.text}}>{S().af.accModalPrimaryLabel}</span>
                      <HelpTooltip text={S().af.addAccPrimaryHint} size={14} />
                    </label>
                    <label style={{display:'flex', alignItems:'center', gap:6, cursor:'pointer'}}>
                      <input type="checkbox" checked={nAccHidden} onChange={e=>setNAccHidden(e.target.checked)} style={{width:15, height:15}}/>
                      <span style={{fontSize:13, fontWeight:600, color:t.text}}>{S().af.accModalHiddenLabel}</span>
                      <HelpTooltip text={S().af.addAccHiddenHint} size={14} />
                    </label>
                  </div>
                </div>
              )}

              <div style={{display:'flex', flexWrap:'wrap', gap:16, alignItems:'flex-start', alignContent:'flex-start'}}>
                <div style={{flex:1, minWidth:250, display:'flex', flexDirection:'column', gap:8}}>
                   {sorted.map(a => {
                      const bal = data.accountBalances[a.id] || 0;
                      return (
                        // НОВОЕ: Делаем скрытые счета полупрозрачными (opacity: a.isHidden ? 0.7 : 1)
                        <div key={a.id} onClick={() => setActiveAcc(a)} style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 12px', borderRadius:10, background:t.bgInput, borderLeft:`4px solid ${a.color}`, cursor:'pointer', transition:'filter .2s', opacity: a.isHidden ? 0.7 : 1}} onMouseEnter={e=>e.currentTarget.style.filter='brightness(1.05)'} onMouseLeave={e=>e.currentTarget.style.filter='none'}>
                           <div style={{display:'flex', alignItems:'center', gap:8, flex:1, minWidth:0}}>
                             <div style={{fontSize:14, fontWeight:700, color:t.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{a.name}</div>
                             {/* НОВОЕ: Бейджик Основной и Иконка Скрытый */}
                             {a.isPrimary && <span style={{fontSize:10, background:`${t.accent}15`, color:t.accent, padding:'2px 6px', borderRadius:6, fontWeight:700}}>{S().af.primaryBadge}</span>}
                             {a.isHidden && <EyeOff size={14} color={t.textMuted} title={S().af.hiddenAccountTitle} />}
                           </div>
                           <div style={{display:'flex', alignItems:'center', gap:6, flexShrink:0, marginLeft:8}}>
                             <div className="mono" style={{fontSize:14, fontWeight:800, color:bal>0 ? a.color : t.textSec, marginRight:6}}><AnimNum value={bal}/></div>
                             
                             {/* НОВОЕ: Если счет скрытый, показываем + и - для заначки, иначе стандартный + */}
                             {a.isHidden ? (
                               canE && (
                                 <>
                                   <button onClick={(e) => { e.stopPropagation(); setQHiddenAcc({id: a.id, type: 'income'}); }} title={S().af.addToStash} style={{background:'transparent', color:t.accentGreen, padding:3, border:'none', cursor:'pointer'}}><Plus size={15}/></button>
                                   <button onClick={(e) => { e.stopPropagation(); setQHiddenAcc({id: a.id, type: 'expense'}); }} title={S().af.takeFromStash} style={{background:'transparent', color:t.accentRed, padding:3, border:'none', cursor:'pointer'}}><Minus size={15}/></button>
                                 </>
                               )
                             ) : (
                               canE && <button onClick={(e) => { e.stopPropagation(); setQIncAcc(a.id); }} title={S().af.quickIncomeTitle} style={{background:'transparent', color:t.accentGreen, padding:3, border:'none', cursor:'pointer'}}><Plus size={15}/></button>
                             )}

                             <button onClick={(e) => { e.stopPropagation(); setHist({id:a.id, name:a.name}); }} title={S().af.historyBtn} style={{background:'transparent', color:t.accentCyan, padding:3, border:'none', cursor:'pointer'}}><History size={14}/></button>
                           </div>
                        </div>
                      )
                   })}
                   {aAccs.length === 0 && <div style={{color:t.textMuted, fontSize:13, textAlign:'center', padding:20}}>{S().af.noActiveAccounts}</div>}
                </div>
                {pieAccs.length > 0 && (
                  <div style={{width:130, height:130, flexShrink:0, margin:'auto'}}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={pieAccs} cx="50%" cy="50%" outerRadius="100%" innerRadius="65%" dataKey="value" paddingAngle={3} stroke={t.bgCard} strokeWidth={2}>
                          {pieAccs.map((d, i) => <Cell key={i} fill={d.color}/>)}
                        </Pie>
                        <Tooltip formatter={v=>fmt(v)} contentStyle={{background:t.bgCard, border:`1px solid ${t.border}`, borderRadius:10, fontSize:13, color:t.text}}/>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </Card>

            {/* СПИСОК ФОНДОВ (КЛИКАБЕЛЬНЫЕ СТРОКИ) */}
            <Card>
              <div style={{fontSize: isCompact ? 14 : 16, fontWeight:700, marginBottom:16, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8}}>
                <div style={{display:'flex', alignItems:'center', gap:8}}><FolderOpen size={isCompact ? 16 : 18} color={t.accentPurple}/> {S().af.fundsState}</div>
                <div style={{display:'flex', alignItems:'center', gap:10}}>
                  <div className="mono" style={{fontSize: isCompact ? 13 : 15, color:t.accentPurple, fontWeight:800}}><AnimNum value={tFund}/></div>
                  {canE && <Btn onClick={() => setShowFundManager('manage')} title={S().af.fmTitle || 'Корректировка фондов'} style={{padding:'4px 8px'}}><Sliders size={14}/></Btn>}
                  {canE && <Btn onClick={() => setShowFundManager('add')} style={{padding:'4px 8px'}}><Plus size={14}/></Btn>}
                </div>
              </div>


              <div style={{display:'flex', flexWrap:'wrap', gap:16, alignItems:'flex-start', alignContent:'flex-start'}}>
                <div style={{flex:1, minWidth:250, display:'flex', flexDirection:'column', gap:8}}>
                   {aFunds.map(f => {
                      const bal = data.fundBalances[f.id] || 0;
                      return (
                        <div key={f.id} onClick={() => setActiveFund(f)} style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 12px', borderRadius:10, background:t.bgInput, borderLeft:`4px solid ${f.color}`, cursor:'pointer', transition:'filter .2s'}} onMouseEnter={e=>e.currentTarget.style.filter='brightness(1.05)'} onMouseLeave={e=>e.currentTarget.style.filter='none'}>
                           <div style={{display:'flex', alignItems:'center', gap:6, flex:1, minWidth:0}}>
                             <div style={{fontSize:14, fontWeight:700, color:t.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{f.name}</div>
                             <div style={{fontSize:11, padding:'2px 6px', borderRadius:6, background:`${f.color}18`, color:f.color, fontWeight:700, flexShrink:0}}>{f.percent}%</div>
                           </div>
                           <div style={{display:'flex', alignItems:'center', gap:6, flexShrink:0, marginLeft:8}}>
                             <div className="mono" style={{fontSize:14, fontWeight:800, color: bal>0 ? f.color : t.textSec, marginRight:6}}><AnimNum value={bal}/></div>
                             {canE && <button onClick={(e) => { e.stopPropagation(); setQExpFund(f.id); }} title={S().af.quickExpenseTitle} style={{background:'transparent', color:t.accentRed, padding:3}}><Minus size={15}/></button>}
                             <button onClick={(e) => { e.stopPropagation(); setHist({id:f.id, name:f.name}); }} title={S().af.historyBtn} style={{background:'transparent', color:t.accentCyan, padding:3}}><History size={14}/></button>
                           </div>
                        </div>
                      )
                   })}
                   {aFunds.length === 0 && <div style={{color:t.textMuted, fontSize:13, textAlign:'center', padding:20}}>{S().af.noActiveFunds}</div>}
                </div>
                {pie.length > 0 && (
                  <div style={{width:130, height:130, flexShrink:0, margin:'auto'}}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={pie} cx="50%" cy="50%" outerRadius="100%" innerRadius="65%" dataKey="value" paddingAngle={3} stroke={t.bgCard} strokeWidth={2}>
                          {pie.map((d, i) => <Cell key={i} fill={d.color}/>)}
                        </Pie>
                        <Tooltip formatter={v=>fmt(v)} contentStyle={{background:t.bgCard, border:`1px solid ${t.border}`, borderRadius:10, fontSize:13, color:t.text}}/>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </Card>

          </div>

          <div style={{display:'grid', gridTemplateColumns: isCompact ? '1fr' : '1fr 1fr 1fr', gap:18}}>
            {/* КТО ДОЛЖЕН НАМ (ДЕБИТОРКА) */}
            <Card style={{display:'flex', flexDirection:'column', position:'relative'}}>
              <div style={{position:'absolute', top:12, left:16, fontSize:10, fontWeight:800, color:t.accentGreen, textTransform:'uppercase', opacity:0.8}}>{S().af.receivableLabel}</div>
              <div style={{fontSize:16, fontWeight:700, margin:'12px 0 14px 0', display:'flex', alignItems:'center', gap:8, justifyContent:'space-between'}}>
                <div style={{display:'flex', alignItems:'center', gap:8}}><TrendingUp size={18} color={t.accentGreen}/> {S().af.theyOweUs}</div>
                <button
                  type="button"
                  onClick={() => setShowDebtFundProj(true)}
                  title={S().af.futureFundsHint}
                  className="mono"
                  style={{
                    color: t.accentGreen, fontSize: 14, fontWeight: 700,
                    background: `${t.accentGreen}15`,
                    border: `1px solid ${t.accentGreen}40`,
                    borderRadius: 8, padding: '4px 10px', cursor: 'pointer'
                  }}
                >{fmt((data.debts || []).filter(d => d.type === 'receivable' && d.status !== 'archived' && d.status !== 'paid').reduce((s,d) => s + (d.amount - (d.paidAmount||0)), 0))}</button>
              </div>
              <div style={{flex:1, overflowY:'auto', maxHeight:320}}>
                {(data.debts || []).filter(d => d.type === 'receivable' && d.status !== 'archived' && d.status !== 'paid').length === 0 ? (
                  <div style={{color:t.textMuted, fontSize:12, textAlign:'center', padding:20}}>{S().af.noActiveDebts}</div>
                ) : (
                  (data.debts || []).filter(d => d.type === 'receivable' && d.status !== 'archived' && d.status !== 'paid').slice(0, 10).map(d => {
                    const con = (data.contractors || []).find(c => c.id === d.contractorId);
                    const rem = d.amount - (d.paidAmount || 0);
                    // Task #42: вся строка + иконка → deep-link в конкретный долг
                    const openThisDebt = () => onNavigateDebt ? onNavigateDebt(d.id) : setTab('debts');
                    return (
                      <div key={d.id} onClick={openThisDebt} style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 0', borderBottom:`1px solid ${t.border}40`, cursor:'pointer'}} title="Открыть этот долг">
                        <div style={{display:'flex', alignItems:'center', gap:8, minWidth:0}}>
                          <div style={{width:24, height:24, borderRadius:6, background:`${t.accentGreen}15`, color:t.accentGreen, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}><User size={14}/></div>
                          <div style={{minWidth:0}}>
                            <div style={{fontSize:13, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{con?.name || '—'}</div>
                            <div style={{fontSize:10, color:t.textMuted}}>{S().af.dueDate(fmtDate(d.date))}</div>
                          </div>
                        </div>
                        <div style={{display:'flex', alignItems:'center', gap:8}}>
                          <span style={{fontSize:10, color:t.textMuted}}>{fmtDate(d.date)}</span>
                          <span className="mono" style={{fontSize:12, fontWeight:700}}>{fmt(rem)}</span>
                          <button onClick={e => { e.stopPropagation(); openThisDebt(); }} style={{background:t.bgInput, borderRadius:6, padding:4, color:t.textMuted}} title="Открыть этот долг"><Edit3 size={12}/></button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </Card>

            {/* КОМУ ДОЛЖНЫ МЫ (КРЕДИТОРКА) */}
            <Card style={{display:'flex', flexDirection:'column', position:'relative'}}>
              <div style={{position:'absolute', top:12, left:16, fontSize:10, fontWeight:800, color:t.accentRed, textTransform:'uppercase', opacity:0.8}}>{S().af.payableLabel}</div>
              <div style={{fontSize:16, fontWeight:700, margin:'12px 0 14px 0', display:'flex', alignItems:'center', gap:8, justifyContent:'space-between'}}>
                <div style={{display:'flex', alignItems:'center', gap:8}}><TrendingDown size={18} color={t.accentRed}/> {S().af.weOwe}</div>
                <div className="mono" style={{color:t.accentRed, fontSize:14}}>{fmt((data.debts || []).filter(d => d.type === 'payable' && d.status !== 'archived' && d.status !== 'paid').reduce((s,d) => s + (d.amount - (d.paidAmount||0)), 0))}</div>
              </div>
              <div style={{flex:1, overflowY:'auto', maxHeight:320}}>
                {(data.debts || []).filter(d => d.type === 'payable' && d.status !== 'archived' && d.status !== 'paid').length === 0 ? (
                  <div style={{color:t.textMuted, fontSize:12, textAlign:'center', padding:20}}>{S().af.noActiveDebts}</div>
                ) : (
                  (data.debts || []).filter(d => d.type === 'payable' && d.status !== 'archived' && d.status !== 'paid').slice(0, 10).map(d => {
                    const con = (data.contractors || []).find(c => c.id === d.contractorId);
                    const rem = d.amount - (d.paidAmount || 0);
                    // Task #42: вся строка + иконка → deep-link в конкретный долг
                    const openThisDebt = () => onNavigateDebt ? onNavigateDebt(d.id) : setTab('debts');
                    return (
                      <div key={d.id} onClick={openThisDebt} style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 0', borderBottom:`1px solid ${t.border}40`, cursor:'pointer'}} title="Открыть этот долг">
                        <div style={{display:'flex', alignItems:'center', gap:8, minWidth:0}}>
                          <div style={{width:24, height:24, borderRadius:6, background:`${t.accentRed}15`, color:t.accentRed, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}><User size={14}/></div>
                          <div style={{minWidth:0}}>
                            <div style={{fontSize:13, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{con?.name || '—'}</div>
                            <div style={{fontSize:10, color:t.textMuted}}>{S().af.dueDate(fmtDate(d.date))}</div>
                          </div>
                        </div>
                        <div style={{display:'flex', alignItems:'center', gap:8}}>
                          <span style={{fontSize:10, color:t.textMuted}}>{fmtDate(d.date)}</span>
                          <span className="mono" style={{fontSize:12, fontWeight:700}}>{fmt(rem)}</span>
                          <button onClick={e => { e.stopPropagation(); openThisDebt(); }} style={{background:t.bgInput, borderRadius:6, padding:4, color:t.textMuted}} title="Открыть этот долг"><Edit3 size={12}/></button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </Card>

            {/* ПОСЛЕДНИЕ ОПЕРАЦИИ */}
            <Card style={{display:'flex', flexDirection:'column'}}>
              <div style={{fontSize:16, fontWeight:700, marginBottom:14, display:'flex', alignItems:'center', gap:8}}><Clock size={18} color={t.accent}/> {S().af.recentOps}</div>
              <div style={{flex:1}}>{renderTxList(recent10All, S().af.noOps)}</div>
            </Card>
          </div>

        </div>
      )}

      {vw === 'accounts' && (
        <Card>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16}}>
            <div style={{fontSize:16, fontWeight:700}}>{S().af.tabAccounts} <span style={{color:t.textMuted, fontWeight:400}}>({aAccs.length})</span><span className="mono" style={{fontSize:14, color:t.accent, marginLeft:10}}><AnimNum value={tAcc}/></span></div>
            {canE && <Btn onClick={() => setAddAcc(!addAcc)} style={{padding:'6px 12px'}}><Plus size={14}/></Btn>}
          </div>
          {addAcc && (
            <div style={{display:'flex', flexDirection:'column', gap:10, marginBottom:16, padding:14, background:t.bgInput, borderRadius:12, border:`1px dashed ${t.border}`}}>
              <div style={{display:'flex', gap:8}}>
                <input placeholder={S().af.accountNamePlaceholder} value={nAccN} onChange={e=>setNAccN(e.target.value)} style={{flex:1, minWidth:0}}/>
                <div style={{display:'flex', gap:4}}>
                  <Btn variant="success" onClick={doAddAcc} style={{padding:'6px 10px'}}><Check size={16}/></Btn>
                  <button onClick={()=>setAddAcc(false)} style={{background:'#fee2e2',border:'2px solid #b91c1c',borderRadius:10,padding:5,color:'#b91c1c',cursor:'pointer'}}><X size={16}/></button>
                </div>
              </div>
              <div style={{display:'flex', gap:8, alignItems:'center'}}>
                <input placeholder={S().af.ibanPlaceholder} value={nIban} onChange={e=>setNIban(e.target.value)} onKeyDown={e=>e.key==='Enter'&&doAddAcc()} style={{flex:1, minWidth:0, fontSize:13}}/>
                <HelpTooltip text={S().af.ibanTooltip} size={16} />
              </div>
              <div style={{display:'flex', gap:8, alignItems:'center'}}>
                <input type="number" placeholder="Стартовый баланс ₸" value={nAccBal} onChange={e=>setNAccBal(e.target.value)} onKeyDown={e=>e.key==='Enter'&&doAddAcc()} style={{flex:1, minWidth:0, fontSize:13}}/>
              </div>
              <div style={{display:'flex', gap:16, flexWrap:'wrap'}}>
                <label style={{display:'flex', alignItems:'center', gap:6, cursor:'pointer'}}>
                  <input type="checkbox" checked={nAccPrimary} onChange={e=>setNAccPrimary(e.target.checked)} style={{width:15, height:15}}/>
                  <span style={{fontSize:13, fontWeight:600, color:t.text}}>{S().af.accModalPrimaryLabel}</span>
                  <HelpTooltip text={S().af.addAccPrimaryHint} size={14} />
                </label>
                <label style={{display:'flex', alignItems:'center', gap:6, cursor:'pointer'}}>
                  <input type="checkbox" checked={nAccHidden} onChange={e=>setNAccHidden(e.target.checked)} style={{width:15, height:15}}/>
                  <span style={{fontSize:13, fontWeight:600, color:t.text}}>{S().af.accModalHiddenLabel}</span>
                  <HelpTooltip text={S().af.addAccHiddenHint} size={14} />
                </label>
              </div>
            </div>
          )}
          <div style={{maxHeight:'none'}}>
            {sorted.map((acc, i) => {
              const bal = data.accountBalances[acc.id] || 0;
              const pct = tAcc > 0 ? (bal / tAcc * 100) : 0;
              return (
                <div key={acc.id} onClick={() => setActiveAcc(acc)} style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'9px 10px', borderRadius:8, marginBottom:2, background:i%2===0?t.bgInput:'transparent', cursor:'pointer', transition:'filter .2s'}} onMouseEnter={e=>e.currentTarget.style.filter='brightness(1.05)'} onMouseLeave={e=>e.currentTarget.style.filter='none'}>
                  <div style={{display:'flex', alignItems:'center', gap:8, flex:1, minWidth:0}}>
                    <div style={{width:10, height:10, borderRadius:3, background:acc.color, flexShrink:0}}/>
                    <span style={{fontSize:14, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{acc.name}</span>
                  </div>
                  <div style={{display:'flex', alignItems:'center', gap:6, flexShrink:0}}>
                    <div style={{width:50, height:4, background:t.border, borderRadius:2, pointerEvents:'none'}}><div style={{height:'100%', background:acc.color, width:`${Math.min(pct, 100)}%`, borderRadius:2}}/></div>
                    <span className="mono" style={{fontSize:13, fontWeight:600, minWidth:110, textAlign:'right', color:bal>0 ? acc.color : t.textMuted}}><AnimNum value={bal}/></span>
                    {canE && <button onClick={(e) => { e.stopPropagation(); setQIncAcc(acc.id); }} title={S().af.quickIncomeTitle} style={{background:'transparent', color:t.accentGreen, padding:3}}><Plus size={15}/></button>}
                    <button onClick={(e) => { e.stopPropagation(); setHist({id:acc.id, name:acc.name}); }} title={S().af.historyBtn} style={{background:'transparent', color:t.accentCyan, padding:3}}><History size={14}/></button>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
      
      {vw === 'funds' && (
        <Card>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16}}>
            <div style={{fontSize:16, fontWeight:700}}>{S().af.tabFunds} <span style={{color:t.textMuted, fontWeight:400}}>({aFunds.length})</span><span className="mono" style={{fontSize:14, color:t.accentPurple, marginLeft:10}}><AnimNum value={tFund}/></span><span style={{fontSize:13, marginLeft:8, color:tPct===100?t.accentGreen:t.accentYellow, fontWeight:700}}>{tPct}%</span></div>
            <div style={{display:'flex', gap:6}}>
              {canE && <Btn onClick={() => setShowFundManager('manage')} title={S().af.fmTitle || 'Корректировка фондов'} style={{padding:'6px 12px'}}><Sliders size={14}/></Btn>}
              {canE && <Btn onClick={() => setShowFundManager('add')} style={{padding:'6px 12px'}}><Plus size={14}/></Btn>}
            </div>
          </div>
          {/* Inline add form removed — now in FundManagerModal */}
          <div style={{maxHeight:'none'}}>
            {aFunds.map((f, i) => {
              const bal = data.fundBalances[f.id] || 0;
              const mx = Math.max(...aFunds.map(x => data.fundBalances[x.id] || 0), 1);
              return (
                <div key={f.id} onClick={() => setActiveFund(f)} style={{padding:'9px 10px', borderRadius:8, marginBottom:2, background:i%2===0?t.bgInput:'transparent', borderLeft:`3px solid ${f.color}`, cursor:'pointer', transition:'filter .2s'}} onMouseEnter={e=>e.currentTarget.style.filter='brightness(1.05)'} onMouseLeave={e=>e.currentTarget.style.filter='none'}>
                  <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                    <div style={{display:'flex', alignItems:'center', gap:8, flex:1, minWidth:0}}>
                      <span style={{fontSize:14}}>{f.name}</span><span style={{fontSize:12, padding:'1px 8px', borderRadius:8, background:`${f.color}18`, color:f.color, fontWeight:700}}>{f.percent}%</span>
                    </div>
                    <div style={{display:'flex', alignItems:'center', gap:6}}>
                      <span className="mono" style={{fontSize:13, fontWeight:600, color:f.color, minWidth:100, textAlign:'right'}}><AnimNum value={bal}/></span>
                      {canE && <button onClick={(e) => { e.stopPropagation(); setQExpFund(f.id); }} title={S().af.quickExpenseTitle} style={{background:'transparent', color:t.accentRed, padding:3}}><Minus size={15}/></button>}
                      <button onClick={(e) => { e.stopPropagation(); setHist({id:f.id, name:f.name}); }} title={S().af.historyBtn} style={{background:'transparent', color:t.accentCyan, padding:3}}><History size={14}/></button>
                    </div>
                  </div>
                  <div style={{height:4, background:t.border, borderRadius:2, marginTop:7, pointerEvents:'none'}}><div style={{height:'100%', borderRadius:2, background:f.color, width:`${Math.min((bal/mx)*100, 100)}%`, transition:'width .4s'}}/></div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* АРХИВ */}
      {vw === 'archive' && (
        <ArchiveTab data={data} uOrg={uOrg} apiSync={apiSync} />
      )}
    </div>
  );
}

// === МОДАЛКА «БУДУЩЕЕ ПОПОЛНЕНИЕ ФОНДОВ» ===
// Показывает, как распределится текущая активная дебиторка по фондам,
// если должники заплатят полностью. НЕ меняет никаких балансов — только расчёт.
// Логика распределения повторяет QuickIncomeModal.submit (Math.round(sum*percent)/100
// с остатком в последний фонд, чтобы сумма частей == сумме).
function DebtFundProjectionModal({ data, aFunds, t, onClose }) {
  const totalReceivable = (data.debts || [])
    .filter(d => d.type === 'receivable' && d.status !== 'archived' && d.status !== 'paid')
    .reduce((s, d) => s + (d.amount - (d.paidAmount || 0)), 0);

  const rows = [];
  if (aFunds.length > 0 && totalReceivable > 0) {
    let allocated = 0;
    aFunds.forEach((f, idx) => {
      let pt = Math.round(totalReceivable * f.percent) / 100;
      if (idx === aFunds.length - 1) {
        pt = Math.round((totalReceivable - allocated) * 100) / 100;
      }
      allocated += pt;
      rows.push({ id: f.id, name: f.name, color: f.color, percent: f.percent, amount: pt });
    });
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onClose}
    >
      <div
        style={{ background: t.bgCard, borderRadius: 14, padding: 20, width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: t.text }}>{S().af.futureFundsTitle}</div>
            <div style={{ fontSize: 12, color: t.textMuted, marginTop: 4 }}>{S().af.futureFundsHint}</div>
          </div>
          <button
            onClick={onClose}
            title={S().common.close}
            style={{ background: '#fee2e2', border: '2px solid #b91c1c', borderRadius: 10, padding: 5, color: '#b91c1c', cursor: 'pointer', flexShrink: 0 }}
          ><X size={16} /></button>
        </div>

        <div style={{ background: `${t.accentGreen}10`, border: `1px solid ${t.accentGreen}30`, borderRadius: 10, padding: '10px 12px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: t.textMuted, fontWeight: 600 }}>{S().af.theyOweUs}</span>
          <span className="mono" style={{ color: t.accentGreen, fontSize: 15, fontWeight: 800 }}>{fmt(totalReceivable)}</span>
        </div>

        {rows.length === 0 ? (
          <div style={{ textAlign: 'center', color: t.textMuted, fontSize: 13, padding: '20px 0' }}>
            {totalReceivable === 0 ? S().af.futureFundsNoDebts : S().af.futureFundsNoFunds}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {rows.map(r => (
              <div
                key={r.id}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 12px', borderRadius: 10,
                  background: `${r.color}12`, border: `1px solid ${r.color}40`
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: r.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: r.color, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                  <span style={{ fontSize: 11, color: t.textMuted, flexShrink: 0 }}>{r.percent}%</span>
                </div>
                <span className="mono" style={{ fontSize: 13, fontWeight: 800, color: r.color, flexShrink: 0 }}>{fmt(r.amount)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// === ОБНОВЛЕННАЯ МОДАЛКА СЧЕТА (С редактированием настроек видимости) ===
function AccountModal({ acc, data, uOrg, t, onClose, onArchive, onShowHistory, apiSync, onSaved }) {
  const [isEdit, setIsEdit] = useState(false);
  const [name, setName] = useState(acc.name);
  
  // НОВОЕ: Стейты для Основного, Скрытого счета и Видимости
  const [isPrimary, setIsPrimary] = useState(acc.isPrimary || false);
  const [isHidden, setIsHidden] = useState(acc.isHidden || false);
  const [visibleTo, setVisibleTo] = useState(acc.visibleTo || 'me');
  const [iban, setIban] = useState(acc.iban || '');

  const [showCPicker, setShowCPicker] = useState(false);
  const [showCatPicker, setShowCatPicker] = useState(false);

  const myContractors = (data.contractors || []).filter(c => !c.archived && c.isCustomer && (c.accountIds?.includes(acc.id) || !c.accountIds?.length));
  const availContractors = (data.contractors || []).filter(c => !c.archived && c.isCustomer && !c.accountIds?.includes(acc.id));

  const myCats = (data.incomeCategories || []).filter(c => !c.archived && c.accountId === acc.id);
  const availCats = (data.incomeCategories || []).filter(c => !c.archived && c.accountId !== acc.id);

  const toggleC = (cId) => {
    // Чанк 20a.5b: вычисляем новый accountIds до uOrg и вызываем API.
    // Без syncUpdateContractor smart-merge сотрёт изменения при следующем PUT /sync/org.
    const cur = (data.contractors || []).find(c => c.id === cId);
    if (!cur) return;
    const ids = cur.accountIds || [];
    const newAccountIds = ids.includes(acc.id) ? ids.filter(x => x !== acc.id) : [...ids, acc.id];
    uOrg(p => ({ ...p, contractors: p.contractors.map(c => c.id !== cId ? c : { ...c, accountIds: newAccountIds }) }));
    if (apiSync && apiSync.syncUpdateContractor) apiSync.syncUpdateContractor(cId, { accountIds: newAccountIds });
  };
  // Stage 20b B.3: REST PUT /categories для persist accountId изменения.
  const toggleCat = (cId) => {
    const cur = (data.incomeCategories || []).find(c => c.id === cId);
    const newAccountId = cur && cur.accountId === acc.id ? '' : acc.id;
    uOrg(p => ({...p, incomeCategories: p.incomeCategories.map(c => c.id===cId ? {...c, accountId: newAccountId} : c)}));
    if (apiSync && apiSync.syncUpdateCategory) apiSync.syncUpdateCategory(cId, { accountId: newAccountId });
  };

  // НОВОЕ: Функция сохранения теперь обновляет все новые поля
  // 🚨 Hotfix 20a.6c.1 (2026-05-14): добавили apiSync.syncUpdateAccount после uOrg.
  // Без этого rename/edit accounts не персистились — bulk PUT smart-merge перетирал
  // state.accounts из БД (где имя ещё старое) → юзер видел старое имя после F5.
  // Funds/contractors уже работали через REST endpoints (20a.4c/20a.5), accounts
  // были забыты в Stage 4.0 — это долг.
  const save = () => {
    if (!name.trim()) return alert(S().af.accModalEnterName);
    if (!iban.trim()) return alert(S().af.accModalEnterIban);

    uOrg(p => ({ 
      ...p, 
      accounts: p.accounts.map(a => {
        if (a.id === acc.id) return { ...a, name: name.trim(), isPrimary, isHidden, visibleTo, iban: iban.trim() };
        // Если этот счет стал Основным, снимаем галочку с других счетов
        if (isPrimary) return { ...a, isPrimary: false }; 
        return a;
      }) 
    })); 

    // Hotfix 20a.6c.1: персист в Account таблицу через REST. Backend whitelist
    // расширен accept'ить iban + visibleTo (см. server/routes/accounts.js PUT).
    if (apiSync && apiSync.syncUpdateAccount) {
      apiSync.syncUpdateAccount(acc.id, {
        name: name.trim(),
        isPrimary,
        isHidden,
        visibleTo,
        iban: iban.trim(),
      });
    }
    // Fix (2026-06-09): обновить снимок → открытая модалка показывает новое сразу.
    if (onSaved) onSaved({ ...acc, name: name.trim(), isPrimary, isHidden, visibleTo, iban: iban.trim() });
    setIsEdit(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => { if (e.target === e.currentTarget) (onClose)(); }}>
      <div onClick={e => e.stopPropagation()} style={{ background: t.bgCard, borderRadius: 20, width: 500, maxHeight: '90vh', padding: 24, display: 'flex', flexDirection: 'column', border: `1px solid ${t.border}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: acc.color || t.text }}>💳 {acc.name}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <HelpTooltip text={HELP_TEXTS.setupAccount || S().af.helpAccount} size={15} />
            <button onClick={() => setIsEdit(!isEdit)} title={S().af.accModalEdit} style={{ background: t.bgInput, color: t.textMuted, padding: 6, borderRadius: 8, border: 'none', cursor: 'pointer', transition: '0.2s' }} onMouseEnter={e => e.currentTarget.style.color = t.accent} onMouseLeave={e => e.currentTarget.style.color = t.textMuted}><Edit3 size={16} /></button>
            <button onClick={onArchive} title={S().af.accModalArchive} style={{ background: t.bgInput, color: t.textMuted, padding: 6, borderRadius: 8, border: 'none', cursor: 'pointer', transition: '0.2s' }} onMouseEnter={e => e.currentTarget.style.color = t.accentOrange} onMouseLeave={e => e.currentTarget.style.color = t.textMuted}><Archive size={16} /></button>
            <button onClick={onClose} title={S().common.close} style={{ background:'#fee2e2', border:'2px solid #b91c1c', borderRadius:10, padding:5, color:'#b91c1c', cursor:'pointer' }}><X size={18} /></button>
          </div>
        </div>

        {isEdit ? (
            // === РЕЖИМ РЕДАКТИРОВАНИЯ ===
            <div style={{ display: 'grid', gap: 12, marginBottom: 20, background:t.bgInput, padding:14, borderRadius:12 }}>
              <input value={name} onChange={e => setName(e.target.value)} style={{ fontSize: 16, fontWeight: 700, padding: 8, borderRadius: 8, border:`1px solid ${t.border}`, background:t.bgCard, color:t.text }} autoFocus/>

              <label style={{display:'flex', alignItems:'center', gap:8, cursor:'pointer', marginTop:8}}>
                <input type="checkbox" checked={isPrimary} onChange={e=>setIsPrimary(e.target.checked)} style={{width:16, height:16}}/>
                <span style={{fontSize: 14, fontWeight: 700, color: t.text}}>{S().af.accModalPrimaryLabel}</span>
              </label>

              <label style={{display:'flex', alignItems:'center', gap:8, cursor:'pointer'}}>
                <input type="checkbox" checked={isHidden} onChange={e=>setIsHidden(e.target.checked)} style={{width:16, height:16}}/>
                <span style={{fontSize: 14, fontWeight: 700, color: t.text}}>{S().af.accModalHiddenLabel}</span>
              </label>

              {/* Настройка видимости при редактировании (показываем только если счет скрытый) */}
              {isHidden && (
                <div style={{marginTop: 8, padding:10, background:t.bgCard, borderRadius:8, border:`1px solid ${t.border}`}}>
                  <label style={{fontSize:11,color:t.textMuted,fontWeight:700,marginBottom:6,display:'flex',alignItems:'center',gap:6}}>{S().af.accModalVisibilityLabel}</label>
                  <select value={visibleTo} onChange={e=>setVisibleTo(e.target.value)} style={{width:'100%', padding:'8px 10px', borderRadius:6, background:t.bgInput, color:t.text, border:`1px solid ${t.border}`, fontSize:13}}>
                    <option value="me">{S().af.accModalVisMe}</option>
                    <option value="me_accountant">{S().af.accModalVisMeAccountant}</option>
                    <option value="admins">{S().af.accModalVisAdmins}</option>
                    <option value="all">{S().af.accModalVisAll}</option>
                  </select>
                </div>
              )}

              <div style={{marginTop: 8}}>
                <label style={{fontSize:11,color:t.textMuted,fontWeight:700,marginBottom:4,display:'flex',alignItems:'center',gap:6}}>
                  {S().af.accModalIbanLabel}
                </label>
                <div style={{display:'flex', gap:8, alignItems:'center'}}>
                  <input value={iban} onChange={e => setIban(e.target.value)} placeholder="KZ..." style={{ width: '100%', padding: 8, borderRadius: 8, border:`1px solid ${t.border}`, background:t.bgCard, color:t.text }} />
                  <HelpTooltip text={S().af.ibanTooltip} size={16} />
                </div>
              </div>

              <Btn variant="success" style={{justifyContent:'center', marginTop:10}} onClick={save}><Check size={18} /> {S().common.save}</Btn>
            </div>
          ) : (
            // === РЕЖИМ ПРОСМОТРА ===
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h2 style={{ fontSize: 24, fontWeight: 800 }}>{acc.name}</h2>
                {acc.isPrimary && <span style={{fontSize:11, background:`${t.accent}15`, color:t.accent, padding:'2px 8px', borderRadius:8, fontWeight:700}}>{S().af.primaryBadge}</span>}
                {acc.isHidden && <EyeOff size={18} color={t.textMuted} title={S().af.hiddenAccountTitle} />}
              </div>
              
              {/* Вывод информации о видимости для скрытого счета */}
              {acc.isHidden && (
                <div style={{ fontSize: 12, color: t.textMuted, marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <EyeOff size={14} /> 
                  {S().af.accModalVisibilityDisplay} {acc.visibleTo === 'me' ? S().af.accModalVisMe : acc.visibleTo === 'me_accountant' ? S().af.accModalVisMeAccountant : acc.visibleTo === 'admins' ? S().af.accModalVisAdmins : S().af.accModalVisAll}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                <div style={{ padding: '4px 12px', background: `${t.accentGreen}15`, color: t.accentGreen, borderRadius: 20, fontSize: 13, fontWeight: 700 }}>{S().af.accModalBalance(fmt(data.accountBalances[acc.id] || 0))}</div>
                <button onClick={onShowHistory} style={{ color: t.accentCyan, background: 'transparent', fontSize: 13, display: 'flex', alignItems: 'center', gap: 5, border: 'none', cursor: 'pointer' }}><History size={14} /> {S().af.historyBtn}</button>
              </div>
              {acc.iban && (
                <div style={{ fontSize: 12, color: t.textMuted, marginTop: 10, padding: '10px 14px', background: t.bgInput, borderRadius: 10, border: `1px solid ${t.border}` }}>
                  <span style={{fontWeight: 700}}>{S().af.accModalIbanDisplay}</span> {acc.iban}
                </div>
              )}
            </div>
          )}

          {!acc.isHidden && (
            <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
              {/* Категории Доходов */}
              <div style={{ marginBottom: 24, borderTop: `1px solid ${t.border}`, paddingTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}><Tags size={16} color={t.accentGreen}/> {S().af.accModalIncomeCategories}</span>
                  <button onClick={() => setShowCatPicker(!showCatPicker)} style={{ color: t.accent, background: 'transparent', fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer' }}><Plus size={14}/> {S().af.accModalAddBtn}</button>
                </div>
                {showCatPicker && <div style={{ background: t.bgInput, padding: 10, borderRadius: 8, marginBottom: 12, maxHeight: 150, overflowY: 'auto' }}>
                  {availCats.length === 0 ? <div style={{fontSize:12, color:t.textMuted}}>{S().af.accModalNoFreeCategories}</div> : availCats.map(c => <div key={c.id} onClick={() => toggleCat(c.id)} style={{ padding: '8px', cursor: 'pointer', borderBottom: `1px solid ${t.border}`, fontSize: 13 }}>{c.name}</div>)}
                </div>}
                <div style={{ display: 'grid', gap: 6 }}>
                  {myCats.length === 0 ? <div style={{ fontSize: 12, color: t.textMuted }}>{S().af.accModalNoLinkedCategories}</div> : myCats.map(c => <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: t.bgInput, borderRadius: 10 }}><span>{c.name}</span><button onClick={() => toggleCat(c.id)} style={{ color: t.accentRed, background: 'transparent', fontSize: 11, border: 'none', cursor: 'pointer' }}>{S().af.accModalRemoveBtn}</button></div>)}
                </div>
              </div>

              {/* Контрагенты */}
              <div style={{ borderTop: `1px solid ${t.border}`, paddingTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}><Users size={16} color={t.accent}/> {S().af.accModalClients}</span>
                  <button onClick={() => setShowCPicker(!showCPicker)} style={{ color: t.accent, background: 'transparent', fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer' }}><Plus size={14}/> {S().af.accModalAddBtn}</button>
                </div>
                {showCPicker && <div style={{ background: t.bgInput, padding: 10, borderRadius: 8, marginBottom: 12, maxHeight: 150, overflowY: 'auto' }}>
                  {availContractors.length === 0 ? <div style={{fontSize:12, color:t.textMuted}}>{S().af.accModalAllClientsLinked}</div> : availContractors.map(c => <div key={c.id} onClick={() => toggleC(c.id)} style={{ padding: '8px', cursor: 'pointer', borderBottom: `1px solid ${t.border}`, fontSize: 13 }}>{c.name}</div>)}
                </div>}
                <div style={{ display: 'grid', gap: 6 }}>
                  {myContractors.length === 0 ? <div style={{ fontSize: 12, color: t.textMuted }}>{S().af.accModalNoLinkedClients}</div> : myContractors.map(c => <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: t.bgInput, borderRadius: 10 }}><span>{c.name}</span><button onClick={() => toggleC(c.id)} style={{ color: t.accentRed, background: 'transparent', fontSize: 11, border: 'none', cursor: 'pointer' }}>{S().af.accModalRemoveBtn}</button></div>)}
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  );
}

// === МОДАЛКА ФОНДА ===
function FundModal({ fund, data, uOrg, t, tPct, onClose, onArchive, onShowHistory, apiSync }) {
  const [isEdit, setIsEdit] = useState(false);
  const [name, setName] = useState(fund.name);
  const [percent, setPercent] = useState(fund.percent.toString());
  // F4+ (2026-06-08): «показываемые» имя/процент — обновляются при сохранении, чтобы
  // имя в модалке менялось сразу после ОК (не ждать refetch/закрытие). Безопасно: одно поле.
  const [displayName, setDisplayName] = useState(fund.name);
  const [displayPercent, setDisplayPercent] = useState(fund.percent);
  const [showCatPicker, setShowCatPicker] = useState(false);

  const myCats = (data.expenseCategories || []).filter(c => !c.archived && c.fundId === fund.id);
  const availCats = (data.expenseCategories || []).filter(c => !c.archived && c.fundId !== fund.id);

  const save = async () => {
    const pct = parseFloat(percent);
    if (!name.trim() || isNaN(pct) || pct < 0) return;
    const other = data.funds.filter(x => !x.archived && x.id !== fund.id).reduce((s, x) => s + x.percent, 0);
    if (other + pct > 100) return alert(S().af.fundModalExceedsHundred);
    if (other + pct < 100) return alert(S().af.fundModalBelowHundred((other + pct).toFixed(1), (100 - other - pct).toFixed(1)));
    // F3+ (2026-06-08): single-source — без оптимистики, ждём backend → событие refetch.
    setDisplayName(name.trim());
    setDisplayPercent(pct);
    setIsEdit(false);
    if (apiSync && apiSync.syncUpdateFund) await apiSync.syncUpdateFund(fund.id, { name: name.trim(), percent: pct });
    try { window.dispatchEvent(new CustomEvent('inmonex-refetch-funds')); } catch (e) {}
  };

  // Stage 20b B.3: REST PUT /categories для persist fundId изменения.
  const toggleCat = (cId) => {
    const cur = (data.expenseCategories || []).find(c => c.id === cId);
    const newFundId = cur && cur.fundId === fund.id ? '' : fund.id;
    uOrg(p => ({...p, expenseCategories: p.expenseCategories.map(c => c.id===cId ? {...c, fundId: newFundId} : c)}));
    if (apiSync && apiSync.syncUpdateCategory) apiSync.syncUpdateCategory(cId, { fundId: newFundId });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => { if (e.target === e.currentTarget) (onClose)(); }}>
      <div onClick={e => e.stopPropagation()} style={{ background: t.bgCard, borderRadius: 20, width: 500, maxHeight: '90vh', padding: 24, display: 'flex', flexDirection: 'column', border: `1px solid ${t.border}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: fund.color || t.accentPurple }}>📊 {displayName}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <HelpTooltip text={HELP_TEXTS.setupFund || S().af.helpFund} size={15} />
            <button onClick={() => setIsEdit(!isEdit)} title={S().af.accModalEdit} style={{ background: t.bgInput, color: t.textMuted, padding: 6, borderRadius: 8, border: 'none', cursor: 'pointer', transition: '0.2s' }} onMouseEnter={e => e.currentTarget.style.color = t.accent} onMouseLeave={e => e.currentTarget.style.color = t.textMuted}><Edit3 size={16} /></button>
            <button onClick={onArchive} title={S().af.accModalArchive} style={{ background: t.bgInput, color: t.textMuted, padding: 6, borderRadius: 8, border: 'none', cursor: 'pointer', transition: '0.2s' }} onMouseEnter={e => e.currentTarget.style.color = t.accentOrange} onMouseLeave={e => e.currentTarget.style.color = t.textMuted}><Archive size={16} /></button>
            <button onClick={onClose} title={S().common.close} style={{ background:'#fee2e2', border:'2px solid #b91c1c', borderRadius:10, padding:5, color:'#b91c1c', cursor:'pointer' }}><X size={18} /></button>
          </div>
        </div>

        <div style={{ overflowY: 'auto' }}>
          {isEdit ? (
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              <input value={name} onChange={e => setName(e.target.value)} style={{ flex: 2, fontSize: 16, padding: 8, borderRadius: 8 }} autoFocus/>
              <input type="number" value={percent} onChange={e => setPercent(e.target.value)} style={{ width: 80, fontSize: 16, padding: 8, borderRadius: 8 }} />
              <Btn variant="success" onClick={save}><Check size={18} /></Btn>
            </div>
          ) : (
            <div style={{ marginBottom: 20 }}>
              <h2 style={{ fontSize: 24, fontWeight: 800 }}>{displayName} <span style={{fontSize: 16, color: t.textMuted}}>{displayPercent}%</span></h2>
              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <div style={{ padding: '4px 12px', background: `${t.accentPurple}15`, color: t.accentPurple, borderRadius: 20, fontSize: 13, fontWeight: 700 }}>{S().af.fundModalInFund(fmt(data.fundBalances[fund.id] || 0))}</div>
                <button onClick={onShowHistory} style={{ color: t.accentCyan, background: 'transparent', fontSize: 13, display: 'flex', alignItems: 'center', gap: 5, border: 'none' }}><History size={14} /> {S().af.historyBtn}</button>
              </div>
            </div>
          )}

          <div style={{ borderTop: `1px solid ${t.border}`, paddingTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}><Tags size={16} color={t.accentPurple}/> {S().af.fundModalExpenseCategories}</span>
              <button onClick={() => setShowCatPicker(!showCatPicker)} style={{ color: t.accent, background: 'transparent', fontSize: 12, fontWeight: 700, border: 'none' }}><Plus size={14}/> {S().af.accModalAddBtn}</button>
            </div>
            {showCatPicker && <div style={{ background: t.bgInput, padding: 10, borderRadius: 8, marginBottom: 12, maxHeight: 150, overflowY: 'auto' }}>
              {availCats.length === 0 ? <div style={{fontSize:12, color:t.textMuted}}>{S().af.fundModalNoFreeCategories}</div> : availCats.map(c => <div key={c.id} onClick={() => toggleCat(c.id)} style={{ padding: '8px', cursor: 'pointer', borderBottom: `1px solid ${t.border}`, fontSize: 13 }}>{c.name}</div>)}
            </div>}
            <div style={{ display: 'grid', gap: 6 }}>
              {myCats.length === 0 ? <div style={{ fontSize: 12, color: t.textMuted }}>{S().af.fundModalNoLinkedCategories}</div> : myCats.map(c => <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: t.bgInput, borderRadius: 10 }}><span>{c.name}</span><button onClick={() => toggleCat(c.id)} style={{ color: t.accentRed, background: 'transparent', fontSize: 11, border: 'none' }}>{S().af.fundModalRemoveBtn}</button></div>)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// === ОБНОВЛЕННАЯ МОДАЛКА БЫСТРОГО ПОСТУПЛЕНИЯ (С Обязательствами и подсказками) ===
// Экспортируем для использования в DataTab (Справочники)
export function QuickIncomeModal({ data, uOrg, user, accId, onClose, apiSync }) {
  const t = useTheme();

  const [opSource, setOpSource] = useState('manual');
  const [debtId, setDebtId] = useState('');
  const [amt, setAmt] = useState('');
  const [catId, setCatId] = useState('');
  const [conId, setConId] = useState('');
  const [cmt, setCmt] = useState('');
  const [showNewContractor, setShowNewContractor] = useState(false);
  // P1.3.2 (2026-05-21): submit-lock против двойного клика «Поступление».
  // Двойной submit = две Operation в БД = двойное зачисление на счёт + двойное движение фондов.
  const submittingRef = useRef(false);
  const [saving, setSaving] = useState(false);

  const acc = (data.accounts || []).find(a => a.id === accId);

  const activeDebts = (data.debts || []).filter(d => (d.status === 'active' || d.status === 'partial') && d.type === 'receivable');
  const incCats = (data.incomeCategories || []).filter(c => !c.archived && (c.accountId === accId || !c.accountId));
  const clients = (data.contractors || []).filter(c => !c.archived && c.isCustomer !== false);

  const handleDebtChange = (val) => {
    setDebtId(val);
    if (val) {
      const d = activeDebts.find(x => x.id === val);
      if (d) {
        const remaining = d.amount - (d.paidAmount || 0);
        setAmt(String(remaining > 0 ? remaining : d.amount));
        setCatId(d.categoryId || '');
        setConId(d.contractorId || '');
        setCmt(d.description || S().af.qIncDebtPayComment);
      }
    } else {
      setAmt(''); setCatId(''); setConId(''); setCmt('');
    }
  };

  // O3 (2026-06-10): sequential — await POST, при успехе reload-org; при сбое фантома нет.
  const submit = async () => {
    if (submittingRef.current) return;
    const sum = parseFloat(amt);
    if (!sum || sum <= 0) { alert(S().af.qIncEnterAmount); return; }
    if (!apiSync) { alert((S().common && S().common.unknownError) || 'Нет связи с сервером'); return; }

    submittingRef.current = true;
    setSaving(true);

    const cat = incCats.find(c => c.id === catId);
    const con = clients.find(c => c.id === conId);
    const propsFunds = data.funds || [];
    const activeFunds = propsFunds.filter(f => !f.archived);
    const bd = [];
    if (activeFunds.length > 0) {
      activeFunds.forEach((f, idx) => {
        let pt = Math.round(sum * f.percent) / 100;
        if (idx === activeFunds.length - 1) {
          const allocated = bd.reduce((s, b) => s + b.amount, 0);
          pt = Math.round((sum - allocated) * 100) / 100;
        }
        bd.push({ fundId: f.id, fundName: f.name, amount: pt });
      });
    }
    const builtTx = {
      id: uid(), type: 'income', accountId: accId, amount: sum, comment: cmt,
      breakdown: bd, incomeCategoryId: cat?.id || '', incomeCategoryName: cat?.name || '',
      contractorId: con?.id || '', contractorName: con?.name || '',
      userId: user?.id || 'sys', userName: user?.name || S().common.defaultUser,
      date: new Date().toISOString(), debtId: debtId || undefined,
    };
    if (debtId) builtTx.debtPayment = { paymentRecord: { userName: user?.name || 'system' } };

    let result = null;
    try { result = await apiSync.syncCreateOperation(builtTx); } catch (e) { result = null; }
    if (!result) { submittingRef.current = false; setSaving(false); return; }

    addAuditLog(uOrg, user, { type: 'income', desc: `Поступление: ${fmt(sum)} на счет ${acc?.name}`, after: { amount: sum, account: acc?.name, category: cat?.name } });
    submittingRef.current = false;
    setSaving(false);
    onClose();
    try { window.dispatchEvent(new CustomEvent('inmonex-reload-org')); } catch (e) {}
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.6)',zIndex:1500,display:'flex',alignItems:'center',justifyContent:'center'}} onMouseDown={e => { if (e.target === e.currentTarget) (onClose)(); }}>
      <div onClick={e=>e.stopPropagation()} style={{background:t.bgCard,border:`2px solid ${t.accentGreen}`,borderRadius:16,padding:24,width:440,maxWidth:'95vw',maxHeight:'90vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,.3)'}}>
        
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
          <div style={{fontSize:18,fontWeight:800,color:t.accentGreen}}>{S().af.qIncTitle}</div>
          <div style={{display:'flex',alignItems:'center',gap:6}}>
            <HelpTooltip text={HELP_TEXTS.quickIncome} size={15} />
            <button onClick={onClose} style={{background:'#fee2e2',border:'2px solid #b91c1c',borderRadius:10,padding:5,color:'#b91c1c',cursor:'pointer'}}><X size={18}/></button>
          </div>
        </div>



        <div style={{display:'flex',justifyContent:'space-between',marginBottom:14, paddingBottom:14, borderBottom:`1px solid ${t.border}`}}>
          <div><div style={{fontSize:12,color:t.textMuted}}>{S().af.qIncAccountLabel}</div><div style={{fontSize:15,fontWeight:700,color:t.text}}>{acc?.name}</div></div>
          <div style={{textAlign:'right'}}><div style={{fontSize:12,color:t.textMuted}}>{S().af.qIncCurrentBalance}</div><div className="mono" style={{fontSize:15,fontWeight:700,color:t.accentGreen}}>{fmt(data.accountBalances[accId]||0)}</div></div>
        </div>

        <div style={{display:'grid',gap:10,marginBottom:18}}>

          {/* Source toggle: ручной ввод / погашение обязательства */}
          {activeDebts.length > 0 && (
            <div style={{display:'flex',gap:6,background:t.bgInput,padding:4,borderRadius:12}}>
              <button onClick={()=>{setOpSource('manual');setDebtId('');setAmt('');setCmt('');setConId('');}} style={{flex:1,padding:'8px 12px',borderRadius:8,background:opSource==='manual'?t.bgCard:'transparent',color:opSource==='manual'?t.text:t.textMuted,fontWeight:opSource==='manual'?700:500,border:'none',cursor:'pointer',boxShadow:opSource==='manual'?t.shadow:'none',fontSize:13}}>{'✍️ '}{S().ops.freeOperation}</button>
              <button onClick={()=>setOpSource('debt')} style={{flex:1,padding:'8px 12px',borderRadius:8,background:opSource==='debt'?t.bgCard:'transparent',color:opSource==='debt'?t.accent:t.textMuted,fontWeight:opSource==='debt'?700:500,border:'none',cursor:'pointer',boxShadow:opSource==='debt'?t.shadow:'none',fontSize:13}}>{'📥 '}{S().ops.debtPaymentIncoming}</button>
            </div>
          )}

          {/* Выбор обязательства */}
          {opSource === 'debt' && (
            <div style={{padding:12,background:`${t.accentGreen}15`,borderRadius:10,border:`1px solid ${t.accentGreen}40`}}>
              <label style={{fontSize:12,color:t.accentGreen,display:'block',marginBottom:5,fontWeight:700}}>{S().ops.selectDebtReceivable}</label>
              <SearchableSelect
                value={debtId}
                onChange={handleDebtChange}
                options={activeDebts.map(d => {
                  const c = clients.find(x => x.id === d.contractorId);
                  const remaining = d.amount - (d.paidAmount || 0);
                  return { value: d.id, label: `${c ? c.name : S().common.unknown} — ${d.description || ''}`, subLabel: S().ops.remainLabel(fmt(remaining)) };
                })}
                placeholder={S().ops.selectDebtPlaceholder}
              />
            </div>
          )}

          {/* Контрагент с кнопкой + */}
          <div>
            <label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:4,display:'block'}}>{S().ops.contractorOptional}</label>
            <div style={{display:'flex',gap:8,alignItems:'flex-start'}}>
              <div style={{flex:1}}>
                <SearchableSelect
                  value={conId}
                  onChange={setConId}
                  options={clients.map(c => ({ value: c.id, label: c.name, subLabel: c.bin }))}
                  placeholder={S().af.qIncClientNone}
                />
              </div>
              <button onClick={()=>setShowNewContractor(true)} title={S().ops.addContractorQuick} style={{padding:'9px 10px',borderRadius:10,background:t.accent,color:t.btnText,border:'none',cursor:'pointer',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center'}}><Plus size={18}/></button>
            </div>
          </div>

          {/* Категория дохода */}
          <div>
            <label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:4,display:'block'}}>{S().af.qIncCategoryLabel}</label>
            <select value={catId} onChange={e=>setCatId(e.target.value)} style={{width:'100%',padding:'8px 10px',borderRadius:8,background:t.bgInput,color:t.text,border:`1px solid ${t.border}`}}>
              <option value="">{S().af.qIncNoCategory}</option>
              {incCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {/* Сумма */}
          <div>
            <label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:4,display:'block'}}>{S().af.qIncAmountLabel} <span style={{color:t.accentRed}}>*</span></label>
            <NumInput value={amt} onChange={setAmt} />
          </div>

          {/* Комментарий */}
          <div>
            <label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:4,display:'block'}}>{S().af.qIncCommentLabel}</label>
            <input value={cmt} onChange={e=>setCmt(e.target.value)} placeholder={S().af.qIncCommentPlaceholder} style={{width:'100%',padding:'8px 10px',borderRadius:8,background:t.bgInput,color:t.text,border:`1px solid ${t.border}`}}/>
          </div>
        </div>

        <Btn variant="success" onClick={submit} disabled={saving} style={{width:'100%',justifyContent:'center',padding:14,fontSize:15}}>
          <Plus size={18}/> {saving ? 'Сохранение...' : `${S().af.qIncSubmit} ${amt ? fmt(parseFloat(amt)) : ''}`}
        </Btn>
      </div>
      {showNewContractor && <QuickContractorModal data={data} uOrg={uOrg} user={user} apiSync={apiSync} onClose={()=>setShowNewContractor(false)} onCreated={(id)=>setConId(id)} />}
    </div>
  );
}

// Экспортируем для использования в DataTab (Справочники)
export function QuickExpenseModal({ data, uOrg, user, aAccs, fundId, onClose, apiSync }) {
  const t = useTheme();
  const fund = data.funds.find(f => f.id === fundId);
  const [accId, setAccId] = useState((aAccs.find(a => a.isPrimary) || aAccs[0])?.id || '');
  const expenseCategories = (data.expenseCategories || []).filter(cat => cat.fundId === fundId || cat.fundId === '');
  const [catId, setCatId] = useState(expenseCategories[0]?.id || '');
  const [amt, setAmt] = useState('');
  const [cmt, setCmt] = useState('');
  const [contractorId, setContractorId] = useState('');
  const [showNewContractor, setShowNewContractor] = useState(false);
  // Обязательства (payable) привязанные к этому фонду или без привязки
  const [debtId, setDebtId] = useState('');
  // P1.3.2 (2026-05-21): submit-lock против двойного клика «Расход».
  // Двойной submit = две Operation = двойное списание со счёта + двойное движение фонда.
  const submittingRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const activePayables = (data.debts || []).filter(d =>
    (d.status === 'active' || d.status === 'partial') && d.type === 'payable' && (!d.fundId || d.fundId === fundId)
  );

  // Автозаполнение при выборе обязательства
  const handleDebtChange = (e) => {
    const val = e.target.value;
    setDebtId(val);
    if (val) {
      const d = activePayables.find(x => x.id === val);
      if (d) {
        const remaining = d.amount - (d.paidAmount || 0);
        setAmt(String(remaining > 0 ? remaining : d.amount));
        if (d.categoryId) setCatId(d.categoryId);
        if (d.contractorId) setContractorId(d.contractorId);
        setCmt(d.description || S().af.qExpPayDebtComment);
      }
    } else {
      setAmt(''); setCmt(''); setContractorId('');
    }
  };

  // O3 (2026-06-10): sequential — await POST, при успехе reload-org.
  const submit = async () => {
    if (submittingRef.current) return;
    const sum = parseFloat(amt);
    if (!sum || sum <= 0) { alert(S().af.qExpEnterAmount); return; }
    const fb = data.fundBalances[fundId] || 0;
    if (sum > fb) { alert(S().af.qExpFundInsufficient(fund.name, fmt(fb))); return; }
    const ab = data.accountBalances[accId] || 0;
    if (sum > ab) { alert(S().af.qExpAccountInsufficient(fmt(ab))); return; }
    if (!apiSync) { alert((S().common && S().common.unknownError) || 'Нет связи с сервером'); return; }

    submittingRef.current = true;
    setSaving(true);

    const cat = expenseCategories.find(c => c.id === catId);
    const accName = (data.accounts || []).find(a => a.id === accId)?.name;
    const builtTx = { id: uid(), type: 'expense', accountId: accId, fundId, fundName: fund?.name, amount: sum, comment: cmt, contractorId: contractorId || undefined, expenseCategoryId: cat?.id || '', expenseCategoryName: cat?.name || '', userId: user?.id || 'sys', userName: user?.name || S().common.defaultUser, date: new Date().toISOString(), debtId: debtId || undefined };
    if (debtId) builtTx.debtPayment = { paymentRecord: { userName: user?.name || 'system' } };

    let result = null;
    try { result = await apiSync.syncCreateOperation(builtTx); } catch (e) { result = null; }
    if (!result) { submittingRef.current = false; setSaving(false); return; }

    addAuditLog(uOrg, user, { type: 'expense', desc: `Расход: ${fmt(sum)} со счета ${accName}`, after: { amount: sum, account: accName, fund: fund?.name } });
    submittingRef.current = false;
    setSaving(false);
    onClose();
    try { window.dispatchEvent(new CustomEvent('inmonex-reload-org')); } catch (e) {}
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}} onMouseDown={e => { if (e.target === e.currentTarget) (onClose)(); }}>
      <div onClick={e=>e.stopPropagation()} style={{background:t.bgCard,border:`1.5px solid ${t.border}`,borderRadius:16,padding:22,width:420,maxWidth:'90vw',boxShadow:'0 20px 60px rgba(0,0,0,.3)'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
          <div style={{fontSize:18,fontWeight:800}}>{S().af.qExpTitle}</div>
          <button onClick={onClose} style={{background:'#fee2e2',border:'2px solid #b91c1c',borderRadius:10,padding:5,color:'#b91c1c',cursor:'pointer'}}><X size={18}/></button>
        </div>
        <div style={{display:'flex',justifyContent:'space-between',marginBottom:14}}>
          <div><div style={{fontSize:13,color:t.textMuted}}>{S().af.qExpFundLabel}</div><div style={{fontSize:15,fontWeight:700,color:t.accentPurple}}>{fund?.name}</div></div>
          <div style={{textAlign:'right'}}><div style={{fontSize:13,color:t.textMuted}}>{S().af.qExpFundAvailable}</div><div className="mono" style={{fontSize:15,fontWeight:700,color:t.text}}>{fmt(data.fundBalances[fundId]||0)}</div></div>
        </div>
        <div style={{display:'grid',gap:10,marginBottom:18}}>
          {/* Погашение обязательства — показываем если есть payable долги */}
          {activePayables.length > 0 && (
            <div><label style={{fontSize:12,color:t.textMuted,marginBottom:4,display:'block'}}>{S().af.qExpPayDebtLabel}</label>
              <select value={debtId} onChange={handleDebtChange} style={{width:'100%'}}>
                <option value="">{S().af.qExpNoDebtBinding}</option>
                {activePayables.map(d => {
                  const remaining = d.amount - (d.paidAmount || 0);
                  return <option key={d.id} value={d.id}>{d.description || S().af.qExpDebtDefault} — {S().af.qExpDebtRemainder(fmt(remaining))}</option>;
                })}
              </select>
            </div>
          )}
          <div><label style={{fontSize:12,color:t.textMuted,marginBottom:4,display:'block'}}>{S().af.qExpAccountLabel}</label>
            <select value={accId} onChange={e=>setAccId(e.target.value)} style={{width:'100%'}}>
              {aAccs.map(a=><option key={a.id} value={a.id}>{a.name} ({fmt(data.accountBalances[a.id]||0)})</option>)}
            </select>
          </div>
          <div>
            <label style={{fontSize:12,color:t.textMuted,marginBottom:4,display:'block'}}>{S().ops.contractorOptional}</label>
            <div style={{display:'flex',gap:8,alignItems:'flex-start'}}>
              <div style={{flex:1}}>
                <SearchableSelect
                  value={contractorId}
                  onChange={setContractorId}
                  options={(data.contractors || []).filter(c => !c.archived).map(c => ({ value: c.id, label: c.name, subLabel: c.bin }))}
                  placeholder={S().ops.noContractor}
                />
              </div>
              <button onClick={() => setShowNewContractor(true)} title={S().ops.addContractorQuick} style={{padding:'9px 10px',borderRadius:10,background:t.accent,color:t.btnText,border:'none',cursor:'pointer',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center'}}><Plus size={18}/></button>
            </div>
          </div>
          <div><label style={{fontSize:12,color:t.textMuted,marginBottom:4,display:'block'}}>{S().af.qExpCategoryLabel}</label>
            <select value={catId} onChange={e=>setCatId(e.target.value)} style={{width:'100%'}}>
              <option value="">{S().af.qExpNoCategory}</option>
              {expenseCategories.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div><label style={{fontSize:12,color:t.textMuted,marginBottom:4,display:'block'}}>{S().af.qExpAmountLabel} <span style={{color:t.accentRed}}>*</span></label><NumInput value={amt} onChange={setAmt} /></div>
          <div><label style={{fontSize:12,color:t.textMuted,marginBottom:4,display:'block'}}>{S().af.qExpCommentLabel}</label><input value={cmt} onChange={e=>setCmt(e.target.value)} placeholder={S().af.qExpCommentPlaceholder} style={{width:'100%'}}/></div>
        </div>
        <Btn variant="danger" onClick={submit} disabled={saving} style={{width:'100%',justifyContent:'center',padding:14,fontSize:15}}><Minus size={16}/> {saving ? 'Сохранение...' : `${S().af.qExpSubmit} ${amt ? fmt(parseFloat(amt)) : ''}`}</Btn>
      </div>
      {showNewContractor && <QuickContractorModal data={data} uOrg={uOrg} user={user} apiSync={apiSync} onClose={() => setShowNewContractor(false)} onCreated={(id) => setContractorId(id)} />}
    </div>
  );
}

// === НОВОЕ: МОДАЛКА ПОПОЛНЕНИЯ / ИЗЪЯТИЯ ДЛЯ СКРЫТЫХ СЧЕТОВ ===
function QuickHiddenAdjustModal({ data, uOrg, user, accId, type, onClose, apiSync }) {
  const t = useTheme();
  const [amt, setAmt] = useState('');
  const [cmt, setCmt] = useState('');
  // P1.3.2 (2026-05-21): submit-lock против двойного клика на скрытом счёте.
  // Двойной submit = двойное движение баланса скрытого счёта + 2 Operation в БД.
  const submittingRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const acc = (data.accounts || []).find(a => a.id === accId);
  const isInc = type === 'income';

  // O3 (2026-06-10): sequential — await POST, при успехе reload-org.
  const submit = async () => {
    if (submittingRef.current) return;
    const sum = parseFloat(amt);
    if (!sum || sum <= 0) { alert(S().af.qHidEnterAmount); return; }
    const currentBal = data.accountBalances[accId] || 0;
    if (!isInc && sum > currentBal) { alert(S().af.qHidInsufficient(fmt(currentBal))); return; }
    if (!apiSync) { alert((S().common && S().common.unknownError) || 'Нет связи с сервером'); return; }

    submittingRef.current = true;
    setSaving(true);

    const builtTx = {
      id: uid(),
      type: isInc ? 'income' : 'expense',
      accountId: accId,
      amount: sum,
      comment: cmt || (isInc ? S().af.qHidDefaultCommentAdd : S().af.qHidDefaultCommentTake),
      isHidden: true,
      userId: user?.id || 'sys',
      userName: user?.name || S().common.defaultUser,
      date: new Date().toISOString()
    };

    let result = null;
    try { result = await apiSync.syncCreateOperation(builtTx); } catch (e) { result = null; }
    if (!result) { submittingRef.current = false; setSaving(false); return; }

    submittingRef.current = false;
    setSaving(false);
    onClose();
    try { window.dispatchEvent(new CustomEvent('inmonex-reload-org')); } catch (e) {}
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:1500,display:'flex',alignItems:'center',justifyContent:'center'}} onMouseDown={e => { if (e.target === e.currentTarget) (onClose)(); }}>
      <div onClick={e=>e.stopPropagation()} style={{background:t.bgCard,border:`1.5px solid ${isInc ? t.accentGreen : t.accentRed}`,borderRadius:16,padding:24,width:400,maxWidth:'90vw',boxShadow:'0 20px 60px rgba(0,0,0,.3)'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
          <div style={{fontSize:18,fontWeight:800}}>{isInc ? S().af.qHidAddTitle : S().af.qHidTakeTitle}</div>
          <button onClick={onClose} style={{background:'#fee2e2',border:'2px solid #b91c1c',borderRadius:10,padding:5,color:'#b91c1c',cursor:'pointer'}}><X size={18}/></button>
        </div>
        <div style={{display:'flex',justifyContent:'space-between',marginBottom:16}}>
          <div><div style={{fontSize:13,color:t.textMuted}}>{S().af.qHidAccountLabel}</div><div style={{fontSize:15,fontWeight:700,color:t.text}}>{acc?.name}</div></div>
          <div style={{textAlign:'right'}}><div style={{fontSize:13,color:t.textMuted}}>{S().af.qHidCurrentBalance}</div><div className="mono" style={{fontSize:15,fontWeight:700,color:t.text}}>{fmt(data.accountBalances[accId]||0)}</div></div>
        </div>
        <div style={{display:'grid',gap:12,marginBottom:20}}>
          <div><label style={{fontSize:12,color:t.textMuted,marginBottom:4,display:'block'}}>{isInc ? S().af.qHidAddAmountLabel : S().af.qHidTakeAmountLabel}</label><NumInput value={amt} onChange={setAmt} /></div>
          <div><label style={{fontSize:12,color:t.textMuted,marginBottom:4,display:'block'}}>{S().af.qHidCommentLabel}</label><input value={cmt} onChange={e=>setCmt(e.target.value)} placeholder={isInc ? S().af.qHidAddPlaceholder : S().af.qHidTakePlaceholder} style={{width:'100%', padding:'10px 14px', borderRadius:8, background:t.bgInput, color:t.text, border:`1px solid ${t.border}`}}/></div>
        </div>
        <Btn variant={isInc ? "success" : "danger"} onClick={submit} disabled={saving} style={{width:'100%',justifyContent:'center',padding:14,fontSize:15}}>
          {isInc ? <Plus size={16}/> : <Minus size={16}/>} {saving ? 'Сохранение...' : `${isInc ? S().af.qHidSubmitAdd : S().af.qHidSubmitTake} ${amt ? fmt(parseFloat(amt)) : ''}`}
        </Btn>
      </div>
    </div>
  );
}

// ══════════════════════ МОДАЛКА: Корректировка балансов фондов ══════════════════════
// ══════════════════════ МОДАЛКА: Управление фондами (полная) ══════════════════════
function FundManagerModal({ data, uOrg, user, aAccs, aFunds, initialMode, onClose, apiSync, onFundsChanged }) {
  const t = useTheme();
  const totalAccounts = aAccs.filter(a => !a.isHidden).reduce((s, a) => s + (data.accountBalances[a.id] || 0), 0);

  // Рабочая копия фондов
  const [funds, setFunds] = useState(() => aFunds.map(f => ({
    id: f.id, name: f.name, percent: f.percent, balance: data.fundBalances[f.id] || 0, color: f.color,
  })));
  const [editMode] = useState('percent'); // F4+ (2026-06-08): зафиксирован percent, режим «по сумме» убран
  const [step, setStep] = useState(initialMode === 'add' ? 'adding' : initialMode?.delete ? 'deleting' : 'main');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false); // F2 sequential: спиннер на «Сохранить»

  // Добавление нового фонда
  const [newF, setNewF] = useState({ name: '', percent: '', color: COLORS[funds.length % COLORS.length] });
  const [addStep, setAddStep] = useState(1); // 1=info, 2=take pct
  const [pctSources, setPctSources] = useState({}); // {fundId: howMuchToTake}

  // Удаление фонда
  const [delId, setDelId] = useState(initialMode?.delete || null);
  const [delStep, setDelStep] = useState(1); // 1=pct targets, 2=balance target
  const [pctTargets, setPctTargets] = useState({});
  const [balTarget, setBalTarget] = useState('');
  // Чанк I (2026-05-19): сохраняем redistribution params для каждого удалённого фонда.
  // confirmDelete() resets pctTargets/balTarget — без этого state save() не знал бы
  // куда переносить balance/percent. Структура: { [deletedFundId]: { pctTargets, balanceTarget } }.
  const [pendingDeletions, setPendingDeletions] = useState({});

  const totalPct = funds.reduce((s, f) => s + (parseFloat(f.percent) || 0), 0);
  const totalFunds = funds.reduce((s, f) => s + (parseFloat(f.balance) || 0), 0);
  const diff = totalAccounts - totalFunds;

  const updateFund = (id, field, val) => setFunds(prev => prev.map(f => f.id === id ? { ...f, [field]: val } : f));
  const moveFund = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= funds.length) return;
    setFunds(prev => { const n = [...prev]; [n[i], n[j]] = [n[j], n[i]]; return n; });
  };

  // Свободные проценты (не распределены по фондам)
  const freePct = +(100 - totalPct).toFixed(2);

  // ─── Сохранение ───
  const save = async () => {
    // Проверка дублей имён фондов
    const names = funds.map(f => f.name.trim().toLowerCase());
    const dupName = names.find((n, i) => names.indexOf(n) !== i);
    if (dupName) {
      setErr(S().af.dupFundNameSave || 'Несколько фондов с одинаковым названием. Переименуйте.');
      return;
    }
    const pctSum = funds.reduce((s, f) => s + (parseFloat(f.percent) || 0), 0);
    if (Math.abs(pctSum - 100) > 0.01) {
      const diff100 = +(100 - pctSum).toFixed(1);
      const action = diff100 > 0
        ? (S().af.fmValPctAction || ((d) => `Добавьте новый фонд на ${d}% или распределите ${d}% по имеющимся фондам.`))(Math.abs(diff100))
        : (S().af.fmValPctOverAction || ((d) => `Уберите лишние ${d}% из имеющихся фондов.`))(Math.abs(diff100));
      setErr((S().af.fmValPctNot100 ? S().af.fmValPctNot100(pctSum.toFixed(1)) : `Сумма процентов: ${pctSum.toFixed(1)}%. Должно быть 100%.`) + ' ' + action);
      return;
    }
    const balSum = funds.reduce((s, f) => s + (parseFloat(f.balance) || 0), 0);
    if (Math.abs(balSum - totalAccounts) > 0.01) {
      const balDiff = +(balSum - totalAccounts).toFixed(2);
      setErr(S().af.fmValBalMismatch
        ? S().af.fmValBalMismatch(fmt(Math.abs(balDiff)))
        : `Баланс фондов ${balDiff > 0 ? 'превышает' : 'меньше'} баланса счетов на ${fmt(Math.abs(balDiff))}. Сумма в фондах должна совпадать с суммой на счетах.`);
      return;
    }
    // Применяем
    // Сохраняем snapshot до uOrg — для diff с new funds (uOrg синхронный setState callback,
    // но мы вычисляем diff ДО, чтобы избежать race с любым другим обновлением state).
    const oldActiveIds = new Set((data.funds || []).filter(f => !f.archived).map(f => f.id));
    const newIds = new Set(funds.map(f => f.id));

    // F2 part3 (2026-06-08): УБРАНА оптимистичная отрисовка фондов (uOrg). Раньше она
    // писала новый фонд/проценты в orgData.funds СРАЗУ, а merge показывал её вместе со
    // stale apiFunds → самопротиворечие (новый фонд из state + старые проценты из БД =
    // 104% / кассовый разрыв до refetch). Теперь single-source: ждём backend (await ниже)
    // → refetch apiFunds → UI показывает ТОЛЬКО подтверждённое из БД. Без догадок.

    // Чанк 20a.4c.1 + F2 sequential (2026-06-08): SEQUENTIAL — собираем point-вызовы,
    // ЖДЁМ commit (await Promise.all; функции возвращают Promise после F1), потом refetch
    // apiFunds (единственный источник) и только потом закрываем. UI показывает только
    // подтверждённые backend проценты — нет ни «120%» (create), ни «102%» (delete+redistribute).
    if (apiSync) {
      setSaving(true);
      const calls = [];
      for (const f of funds) {
        const pct = parseFloat(f.percent) || 0;
        if (oldActiveIds.has(f.id)) {
          if (apiSync.syncUpdateFund) calls.push(apiSync.syncUpdateFund(f.id, { name: f.name, percent: pct, color: f.color }));
        } else {
          if (apiSync.syncCreateFund) calls.push(apiSync.syncCreateFund({ id: f.id, name: f.name, percent: pct, color: f.color, balance: 0 }));
        }
      }
      for (const oldId of oldActiveIds) {
        if (!newIds.has(oldId)) {
          const redistribute = pendingDeletions[oldId];
          if (redistribute && apiSync.syncArchiveFundWithRedistribute) {
            calls.push(apiSync.syncArchiveFundWithRedistribute(oldId, {
              pctTargets: redistribute.pctTargets || {},
              balanceTarget: redistribute.balanceTarget || null,
            }));
          } else if (apiSync.syncArchiveFund) {
            calls.push(apiSync.syncArchiveFund(oldId));
          }
        }
      }
      try {
        await Promise.all(calls);
        if (onFundsChanged) onFundsChanged();
      } finally {
        setSaving(false);
      }
    }

    addAuditLog(uOrg, user, { type: 'fund_adjust', desc: S().af.fmAuditAdjust || 'Корректировка фондов',
      details: funds.map(f => `${f.name}: ${f.percent}% / ${fmt(f.balance)}`).join(', ') });
    onClose();
  };

  // ─── Добавление фонда: подтверждение ───
  const confirmAdd = () => {
    const pct = parseFloat(newF.percent) || 0;
    if (!newF.name.trim() || pct <= 0) return;
    // Проверка дубля имени
    if (funds.some(f => f.name.trim().toLowerCase() === newF.name.trim().toLowerCase())) {
      setErr(S().af.dupFundName || 'Фонд с таким названием уже существует');
      return;
    }
    const needFromOthers = Math.max(0, +(pct - freePct).toFixed(2));
    const taken = Object.values(pctSources).reduce((s, v) => s + (parseFloat(v) || 0), 0);
    if (Math.abs(taken - needFromOthers) > 0.01) {
      setErr(S().af.fmAddPctMismatch ? S().af.fmAddPctMismatch(needFromOthers, taken.toFixed(1)) : `Нужно снять с других фондов ${needFromOthers}%, указано ${taken.toFixed(1)}%`);
      return;
    }
    setErr('');
    const id = 'f_' + uid();
    // Снимаем проценты с источников
    const updated = funds.map(f => {
      const take = parseFloat(pctSources[f.id]) || 0;
      return take > 0 ? { ...f, percent: (parseFloat(f.percent) || 0) - take } : f;
    });
    updated.push({ id, name: newF.name.trim(), percent: pct, balance: 0, color: newF.color });
    setFunds(updated);
    setStep('main');
    setNewF({ name: '', percent: '', color: COLORS[(funds.length + 1) % COLORS.length] });
    setPctSources({});
    setAddStep(1);
  };

  // ─── Удаление фонда: подтверждение ───
  const confirmDelete = () => {
    const delFund = funds.find(f => f.id === delId);
    if (!delFund) return;
    const pctToDistribute = parseFloat(delFund.percent) || 0;
    const givenPct = Object.values(pctTargets).reduce((s, v) => s + (parseFloat(v) || 0), 0);
    if (delStep === 1 && pctToDistribute > 0 && Math.abs(givenPct - pctToDistribute) > 0.01) {
      setErr(S().af.fmDelPctMismatch ? S().af.fmDelPctMismatch(pctToDistribute, givenPct.toFixed(1)) : `Нужно распределить ${pctToDistribute}%, указано ${givenPct.toFixed(1)}%`);
      return;
    }
    if (delStep === 1 && (parseFloat(delFund.balance) || 0) > 0) {
      setErr('');
      setDelStep(2);
      return;
    }
    setErr('');
    // Чанк I (2026-05-19): сохраняем redistribution params ДО reset state.
    // save() прочитает их и вызовет syncArchiveFundWithRedistribute с правильным payload.
    // Без этого backend не знал бы куда переносить balance — оставался бы drift.
    setPendingDeletions(prev => ({
      ...prev,
      [delId]: {
        pctTargets: { ...pctTargets },
        balanceTarget: balTarget || null,
      }
    }));
    // Применяем
    let updated = funds.filter(f => f.id !== delId).map(f => {
      const addPct = parseFloat(pctTargets[f.id]) || 0;
      const addBal = (balTarget === f.id) ? (parseFloat(delFund.balance) || 0) : 0;
      return { ...f, percent: (parseFloat(f.percent) || 0) + addPct, balance: (parseFloat(f.balance) || 0) + addBal };
    });
    setFunds(updated);
    setStep('main');
    setDelId(null);
    setDelStep(1);
    setPctTargets({});
    setBalTarget('');
  };

  // Автораспределение процентов
  const autoDistributePct = (total, targetFunds) => {
    const result = {};
    const each = total / targetFunds.length;
    targetFunds.forEach((f, i) => {
      result[f.id] = i === targetFunds.length - 1 ? +(total - each * (targetFunds.length - 1)).toFixed(2) : +each.toFixed(2);
    });
    return result;
  };

  const inputStyle = (editable) => ({
    width: 80, padding: '6px 8px', borderRadius: 6, border: `1px solid ${t.border}`,
    background: editable ? t.bgCard : t.bgInput, color: editable ? t.text : t.textMuted,
    fontSize: 13, fontWeight: 700, textAlign: 'right', fontFamily: 'JetBrains Mono, monospace',
    opacity: editable ? 1 : 0.6,
  });

  // ═══ РЕНДЕР ═══
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }} onMouseDown={e => { if (e.target === e.currentTarget) (onClose)(); }}>
      <div onClick={e => e.stopPropagation()} style={{ background: t.bgCard, borderRadius: 20, width: 560, maxWidth: '100%', maxHeight: '92vh', display: 'flex', flexDirection: 'column', border: `1px solid ${t.border}`, overflow: 'hidden' }}>

        {/* HEADER */}
        <div style={{ padding: '18px 22px 14px', borderBottom: `1px solid ${t.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Sliders size={20} color={t.accentPurple} />
            <div>
              <div style={{ fontSize: 17, fontWeight: 800 }}>{S().af.fmTitle || 'Корректировка фондов'}</div>
              <div style={{ fontSize: 11, color: t.textMuted }}>{S().af.fmSubtitle || 'Проценты, балансы, порядок, добавление и удаление'}</div>
            </div>
            <HelpTooltip text={S().af.fmHelp || 'Управляйте фондами: меняйте проценты, балансы, порядок. Добавляйте и удаляйте фонды с перераспределением.'} size={15} />
          </div>
          <button onClick={onClose} style={{ background:'#fee2e2', border:'2px solid #b91c1c', borderRadius:10, padding:5, color:'#b91c1c', cursor:'pointer' }}><X size={18} /></button>
        </div>

        <div style={{ padding: '14px 22px', overflowY: 'auto', flex: 1 }}>

          {/* ════ MAIN VIEW ════ */}
          {step === 'main' && <>
            {/* F4+ (2026-06-08): переключатель «Менять баланс» убран — баланс фонда меняется
                только операциями (доход/расход/перевод между фондами), вручную не сохранялся
                и вводил в заблуждение. Остаётся правка процентов; баланс — read-only (инфо). */}

            {/* Список фондов */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
              {/* Заголовок таблицы */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 8px', fontSize: 11, color: t.textMuted, fontWeight: 600 }}>
                <span style={{ width: 44 }}></span>
                <span style={{ flex: 1 }}>{S().af.fmFundName || 'Название'}</span>
                <span style={{ width: 80, textAlign: 'right' }}>%</span>
                <span style={{ width: 100, textAlign: 'right' }}>{S().af.fmBalance || 'Баланс'}</span>
                <span style={{ width: 28 }}></span>
              </div>
              {funds.map((f, i) => (
                <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px', background: t.bgInput, borderRadius: 10, borderLeft: `4px solid ${f.color}` }}>
                  {/* Стрелки перемещения */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    <button disabled={i === 0} onClick={() => moveFund(i, -1)} style={{ background: 'transparent', color: i === 0 ? t.border : t.textMuted, padding: 1, cursor: i === 0 ? 'default' : 'pointer', border: 'none' }}><ChevronUp size={14} /></button>
                    <button disabled={i === funds.length - 1} onClick={() => moveFund(i, 1)} style={{ background: 'transparent', color: i === funds.length - 1 ? t.border : t.textMuted, padding: 1, cursor: i === funds.length - 1 ? 'default' : 'pointer', border: 'none' }}><ChevronDown size={14} /></button>
                  </div>
                  {/* Цвет */}
                  <input type="color" value={f.color} onChange={e => updateFund(f.id, 'color', e.target.value)} style={{ width: 24, height: 24, padding: 0, border: 'none', cursor: 'pointer', borderRadius: 4, flexShrink: 0 }} />
                  {/* Название */}
                  <input value={f.name} onChange={e => updateFund(f.id, 'name', e.target.value)} style={{ flex: 1, minWidth: 0, padding: '5px 8px', borderRadius: 6, border: `1px solid ${t.border}`, background: t.bgCard, color: t.text, fontSize: 13, fontWeight: 600 }} />
                  {/* Проценты */}
                  <input type="number" value={f.percent} onChange={e => updateFund(f.id, 'percent', e.target.value)} disabled={editMode !== 'percent'} style={inputStyle(editMode === 'percent')} />
                  {/* Баланс */}
                  <input type="number" value={f.balance} onChange={e => updateFund(f.id, 'balance', e.target.value)} disabled={editMode !== 'balance'} style={{ ...inputStyle(editMode === 'balance'), width: 100 }} />
                  {/* Удалить */}
                  <button onClick={() => { setDelId(f.id); setDelStep(1); setPctTargets({}); setBalTarget(''); setErr(''); setStep('deleting'); }} style={{ background: 'transparent', color: t.accentRed, padding: 3, cursor: 'pointer', border: 'none', flexShrink: 0, opacity: 0.6 }} title={S().af.fmDeleteFund || 'Удалить фонд'}><Trash2 size={14} /></button>
                </div>
              ))}
            </div>

            {/* Добавить фонд */}
            <button onClick={() => { setStep('adding'); setAddStep(1); setErr(''); setNewF({ name: '', percent: '', color: COLORS[funds.length % COLORS.length] }); setPctSources({}); }} style={{ width: '100%', padding: 10, borderRadius: 10, background: `${t.accentGreen}10`, border: `1.5px dashed ${t.accentGreen}40`, color: t.accentGreen, fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 14 }}>
              <Plus size={15} /> {S().af.fmAddFund || 'Добавить фонд'}
            </button>

            {/* Инфо блок */}
            <div style={{ padding: 12, borderRadius: 10, background: `${t.accent}06`, border: `1px solid ${t.accent}15`, fontSize: 12, marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: t.textMuted }}>{S().af.fmInfoAccounts || 'Сумма на счетах'}</span>
                <span className="mono" style={{ fontWeight: 700, color: t.accent }}>{fmt(totalAccounts)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: t.textMuted }}>{S().af.fmInfoFunds || 'Сумма в фондах'}</span>
                <span className="mono" style={{ fontWeight: 700, color: t.accentPurple }}>{fmt(totalFunds)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 700, color: diff === 0 ? t.accentGreen : diff > 0 ? t.accentOrange : t.accentRed }}>
                  {diff === 0 ? (S().af.fmInfoBalanced || '✓ Совпадает') : (S().af.fmInfoDiff || 'Разница')}
                </span>
                {diff !== 0 && <span className="mono" style={{ fontWeight: 800, color: diff > 0 ? t.accentOrange : t.accentRed }}>{diff > 0 ? '+' : ''}{fmt(diff)}</span>}
              </div>
              <div style={{ fontSize: 11, marginTop: 4 }}>
                <span style={{ fontWeight: 600 }}>{S().af.fmPctTotal || 'Процентов'}: </span>
                <span style={{ color: Math.abs(totalPct - 100) < 0.01 ? t.accentGreen : t.accentRed, fontWeight: 700 }}>{totalPct.toFixed(1)}%</span>
              </div>
            </div>
          </>}

          {/* ════ ADDING FUND ════ */}
          {step === 'adding' && <>
            <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Plus size={18} color={t.accentGreen} /> {addStep === 1 ? (S().af.fmAddStep1 || 'Новый фонд') : (S().af.fmAddStep2 || 'Откуда взять проценты?')}
            </div>

            {addStep === 1 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* Инфо о свободных процентах */}
                {freePct > 0 && (
                  <div style={{ padding: '10px 14px', borderRadius: 8, background: `${t.accentGreen}10`, border: `1px solid ${t.accentGreen}25`, fontSize: 13, color: t.accentGreen, fontWeight: 600 }}>
                    {S().af.fmAddFreePct ? S().af.fmAddFreePct(freePct) : `Свободно ${freePct}% — не распределены по фондам`}
                  </div>
                )}
                {freePct <= 0 && (
                  <div style={{ padding: '10px 14px', borderRadius: 8, background: `${t.accentOrange}10`, border: `1px solid ${t.accentOrange}25`, fontSize: 13, color: t.accentOrange, fontWeight: 600 }}>
                    {S().af.fmAddNoFreePct || 'Все 100% распределены. Проценты для нового фонда будут взяты из существующих.'}
                  </div>
                )}
                <input value={newF.name} onChange={e => setNewF({ ...newF, name: e.target.value })} placeholder={S().af.fundNamePlaceholder || 'Название фонда'} style={{ padding: '10px 14px', borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgInput, color: t.text, fontSize: 14 }} autoFocus />
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input type="number" value={newF.percent} onChange={e => setNewF({ ...newF, percent: e.target.value })} placeholder="%" style={{ width: 80, padding: '10px', borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgInput, color: t.text, fontSize: 14, textAlign: 'center' }} />
                  <span style={{ fontSize: 13, color: t.textMuted }}>%</span>
                  <input type="color" value={newF.color} onChange={e => setNewF({ ...newF, color: e.target.value })} style={{ width: 36, height: 36, padding: 0, border: 'none', cursor: 'pointer', borderRadius: 6 }} />
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                  <button onClick={() => { setStep('main'); setErr(''); }} style={{ flex: 1, padding: 10, borderRadius: 8, background: t.bgInput, color: t.textSec, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none' }}>{S().common.cancel || 'Отмена'}</button>
                  <button onClick={() => {
                    if (!newF.name.trim() || !(parseFloat(newF.percent) > 0)) return;
                    const pct = parseFloat(newF.percent);
                    const needFromOthers = Math.max(0, +(pct - freePct).toFixed(2));
                    if (needFromOthers <= 0) {
                      // Свободных % хватает — сразу добавляем без шага 2
                      setPctSources({});
                      confirmAdd();
                      return;
                    }
                    // Нужно снять часть с других фондов
                    setPctSources(autoDistributePct(needFromOthers, funds));
                    setAddStep(2);
                    setErr('');
                  }} style={{ flex: 1, padding: 10, borderRadius: 8, background: t.accent, color: t.btnText, fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none' }}>{(() => {
                    const pct = parseFloat(newF.percent) || 0;
                    const need = Math.max(0, +(pct - freePct).toFixed(2));
                    return need <= 0 && newF.name.trim() && pct > 0 ? (S().af.fmAddConfirm || 'Добавить') : (S().af.fmAddNext || 'Далее');
                  })()}</button>
                </div>
              </div>
            )}

            {addStep === 2 && (() => {
              const pct = parseFloat(newF.percent) || 0;
              const needFromOthers = Math.max(0, +(pct - freePct).toFixed(2));
              const takenTotal = Object.values(pctSources).reduce((s, v) => s + (parseFloat(v) || 0), 0);
              return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {/* Инфо: свободные % и сколько нужно снять */}
                <div style={{ padding: '10px 14px', borderRadius: 8, background: `${t.accent}08`, border: `1px solid ${t.accent}20`, fontSize: 13 }}>
                  {freePct > 0 && <div style={{ color: t.accentGreen, fontWeight: 600, marginBottom: 4 }}>
                    {S().af.fmAddHasFree ? S().af.fmAddHasFree(freePct) : `У вас есть свободные ${freePct}% (без фонда).`}
                  </div>}
                  <div style={{ color: t.text }}>
                    {S().af.fmAddNeedFromOthers
                      ? S().af.fmAddNeedFromOthers(pct, needFromOthers)
                      : `Новый фонд: ${pct}%. Нужно снять с других фондов: ${needFromOthers}%.`}
                  </div>
                </div>
                {funds.map(f => (
                  <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: t.bgInput, borderRadius: 8, borderLeft: `3px solid ${f.color}` }}>
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{f.name} <span style={{ color: t.textMuted }}>({f.percent}%)</span></span>
                    <input type="number" value={pctSources[f.id] || ''} onChange={e => setPctSources({ ...pctSources, [f.id]: e.target.value })} placeholder="0" style={{ width: 70, padding: '6px 8px', borderRadius: 6, border: `1px solid ${t.border}`, background: t.bgCard, color: t.text, fontSize: 13, textAlign: 'right', fontWeight: 700 }} />
                    <span style={{ fontSize: 12, color: t.textMuted }}>%</span>
                  </div>
                ))}
                <div style={{ fontSize: 12, color: t.textMuted, display: 'flex', justifyContent: 'space-between' }}>
                  <span>{S().af.fmAddNeedTake || 'Нужно снять'}: {needFromOthers}%</span>
                  <span style={{ fontWeight: 700, color: Math.abs(takenTotal - needFromOthers) < 0.01 ? t.accentGreen : t.accentRed }}>
                    {S().af.fmAddTaken || 'Указано'}: {takenTotal.toFixed(1)}%
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                  <button onClick={() => { setAddStep(1); setErr(''); }} style={{ flex: 1, padding: 10, borderRadius: 8, background: t.bgInput, color: t.textSec, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none' }}>{S().af.fmAddBack || 'Назад'}</button>
                  <button onClick={confirmAdd} style={{ flex: 1, padding: 10, borderRadius: 8, background: t.accentGreen, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none' }}>{S().af.fmAddConfirm || 'Добавить'}</button>
                </div>
              </div>
              );
            })()}
          </>}

          {/* ════ DELETING FUND ════ */}
          {step === 'deleting' && (() => {
            const delFund = funds.find(f => f.id === delId);
            if (!delFund) { setStep('main'); return null; }
            const others = funds.filter(f => f.id !== delId);
            return <>
              <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8, color: t.accentRed }}>
                <Trash2 size={18} /> {S().af.fmDelTitle ? S().af.fmDelTitle(delFund.name) : `Удаление фонда «${delFund.name}»`}
              </div>

              {delStep === 1 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
                  <div style={{ fontSize: 13, color: t.textSec }}>{S().af.fmDelStep1Hint ? S().af.fmDelStep1Hint(delFund.percent) : `Распределите ${delFund.percent}% между оставшимися фондами`}</div>
                  <button onClick={() => setPctTargets(autoDistributePct(parseFloat(delFund.percent) || 0, others))} style={{ fontSize: 12, color: t.accent, background: 'transparent', border: 'none', cursor: 'pointer', textDecoration: 'underline', alignSelf: 'flex-start', padding: 0 }}>{S().af.fmDelAutoDistribute || 'Распределить пропорционально'}</button>
                  {others.map(f => (
                    <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: t.bgInput, borderRadius: 8, borderLeft: `3px solid ${f.color}` }}>
                      <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{f.name} <span style={{ color: t.textMuted }}>({f.percent}%)</span></span>
                      <input type="number" value={pctTargets[f.id] || ''} onChange={e => setPctTargets({ ...pctTargets, [f.id]: e.target.value })} placeholder="0" style={{ width: 70, padding: '6px 8px', borderRadius: 6, border: `1px solid ${t.border}`, background: t.bgCard, color: t.text, fontSize: 13, textAlign: 'right', fontWeight: 700 }} />
                      <span style={{ fontSize: 12, color: t.textMuted }}>%</span>
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                    <button onClick={() => { setStep('main'); setErr(''); }} style={{ flex: 1, padding: 10, borderRadius: 8, background: t.bgInput, color: t.textSec, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none' }}>{S().common.cancel || 'Отмена'}</button>
                    <button onClick={confirmDelete} style={{ flex: 1, padding: 10, borderRadius: 8, background: t.accentRed, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none' }}>{(parseFloat(delFund.balance) || 0) > 0 ? (S().af.fmAddNext || 'Далее') : (S().af.fmDelConfirm || 'Удалить')}</button>
                  </div>
                </div>
              )}

              {delStep === 2 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
                  <div style={{ fontSize: 13, color: t.textSec }}>{S().af.fmDelStep2Hint ? S().af.fmDelStep2Hint(fmt(delFund.balance)) : `Куда перевести ${fmt(delFund.balance)}?`}</div>
                  {others.map(f => (
                    <label key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: balTarget === f.id ? `${f.color}12` : t.bgInput, borderRadius: 8, borderLeft: `3px solid ${f.color}`, cursor: 'pointer', border: balTarget === f.id ? `1.5px solid ${f.color}` : `1.5px solid transparent` }}>
                      <input type="radio" name="balTarget" checked={balTarget === f.id} onChange={() => setBalTarget(f.id)} />
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{f.name}</span>
                      <span className="mono" style={{ marginLeft: 'auto', fontSize: 12, color: t.textMuted }}>{fmt(f.balance)}</span>
                    </label>
                  ))}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: balTarget === '__writeoff' ? `${t.accentRed}08` : t.bgInput, borderRadius: 8, cursor: 'pointer', border: balTarget === '__writeoff' ? `1.5px solid ${t.accentRed}40` : `1.5px solid transparent` }}>
                    <input type="radio" name="balTarget" checked={balTarget === '__writeoff'} onChange={() => setBalTarget('__writeoff')} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: t.accentRed }}>{S().af.fmDelWriteOff || 'Списать (обнулить)'}</span>
                  </label>
                  <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                    <button onClick={() => setDelStep(1)} style={{ flex: 1, padding: 10, borderRadius: 8, background: t.bgInput, color: t.textSec, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none' }}>{S().af.fmAddBack || 'Назад'}</button>
                    <button onClick={() => { if (!balTarget) return; setErr(''); confirmDelete(); }} style={{ flex: 1, padding: 10, borderRadius: 8, background: t.accentRed, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none' }}>{S().af.fmDelConfirm || 'Удалить'}</button>
                  </div>
                </div>
              )}
            </>;
          })()}

          {/* Ошибка */}
          {err && <div style={{ padding: '10px 14px', background: `${t.accentRed}10`, border: `1px solid ${t.accentRed}30`, borderRadius: 8, fontSize: 13, color: t.accentRed, fontWeight: 600, marginTop: 8 }}><AlertCircle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />{err}</div>}
        </div>

        {/* FOOTER — только на main */}
        {step === 'main' && (
          <div style={{ padding: '14px 22px', borderTop: `1px solid ${t.border}`, display: 'flex', gap: 10 }}>
            <button onClick={onClose} style={{ flex: 1, padding: 11, borderRadius: 10, background: t.bgInput, color: t.textSec, fontSize: 14, fontWeight: 600, cursor: 'pointer', border: 'none' }}>{S().common.cancel || 'Отмена'}</button>
            <button onClick={save} disabled={saving} style={{ flex: 1, padding: 11, borderRadius: 10, background: t.accent, color: t.btnText, fontSize: 14, fontWeight: 700, cursor: saving ? 'wait' : 'pointer', border: 'none', opacity: saving ? 0.7 : 1 }}>{saving ? 'Сохранение...' : (S().af.fmSave || 'Сохранить')}</button>
          </div>
        )}
      </div>
    </div>
  );
}
